/**
 * OnboardingCanvasEditorV2 - V2-native canvas editor for StepNode screens
 *
 * V2-native canvas editor with StepNode types throughout.
 * Canvas operations (rendering, edges, drag-drop, positions) work natively
 * with V2 StepNode — no V1↔V2 conversion overhead.
 *
 * Double-click opens V2 screen editor dialog (Sprint 3).
 * Native V2 editor sections work directly with StepNode data.
 *
 * Active V2 behavior:
 * - V2ScreenFlowCoordinator (not V1)
 * - v2ScreenRegistry / v2TransitionManager (not V1 singletons)
 * - Coordinator: V2ScreenFlowCoordinator by default; `coordinatorFactory` hosts another
 *   FlowCoordinator (article draft, strategy block). Call only methods EVERY hosted
 *   coordinator has; onboarding-only members are optional capabilities read through
 *   their helpers (isFlowVariablesHost, readFlowAnimationDefaults) — guard test
 *   __tests__/OnboardingCanvasEditorV2.coordinatorCalls.test.ts
 * - Double-click: Opens V2 dialog directly (no adapter conversion)
 * - Editor save: Receives StepNode directly (no V1→V2 conversion)
 * - Copy/paste: V2 clipboard/paste services operate on StepNode data
 *
 * Sprint: schema-v2-migration, Sprint 2 + Sprint 3
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Typography,
  Button,
  Portal,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Stack,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
} from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { useProjectCapability } from '@modules/billing-access';
import { BaseDialog, WithGuideHelp } from '@modules/ui-components';
import { useAuth } from '@frontend/contexts/AuthContext';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { ProjectThemeService } from '@modules/firebase-core/services/ProjectThemeService';
import { ThemeV2Service } from '@modules/firebase-core/services/ThemeV2Service';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';
import { ReactFlowCanvas, ReactFlowCanvasProvider } from '@modules/reactFlow/components/shared/ReactFlowCanvas';
import { CanvasTasksPanel } from '@modules/reactFlow/components/shared/CanvasTasksPanel';
import { OnboardingTemplatePanelV2, type OnboardingTemplatePanelV2Handle } from './OnboardingTemplatePanelV2';
import { OnboardingScreenEditorDialogV2 } from './OnboardingScreenEditorDialogV2';
import { ScreenPickerDialog } from './ScreenPickerDialog';
import { useOnboardingThemeSettingsV2 } from '@modules/onboardings/hooks/useOnboardingThemeSettingsV2';
import { useStrategyThemeSettings } from '@modules/themes/hooks/useStrategyThemeSettings';
import { ApplyThemeGroupsDialog, type ApplyThemeGroupsConfirmPayload } from '@modules/themes/components/ApplyThemeGroupsDialog';
import {
  cloneOnboardingThemeSettings,
  compareThemesV2ByOrder,
  onboardingSettingsV2ToThemeV2,
  themeV2ToOnboardingSettings,
} from '@modules/themes/utils';
import { OnboardingThemeApi } from '@modules/firebase-core/services/OnboardingThemeApi';
import { ALL_THEME_APPLY_GROUPS, type ThemeApplyGroup } from '@modules/themes/mappers/themeApplyGroups';
import { preserveThemeApplyScreenContent } from '@modules/themes/mappers/preserveThemeApplyScreenContent';
import { buildPerKindTransitions } from '@modules/themes/animation/applyAnimationSettingsToFlowDocument';
import { templateTileKey } from '@modules/themes/mappers/templateIdentity';
import { TransitionEditor } from '@modules/reactFlow/components/shared/TransitionEditor';
import { EdgeDeleteConfirmation } from '@modules/reactFlow/components/shared/EdgeDeleteConfirmation';
import { V2ConditionEditorDialog } from '@modules/reactFlow/components/shared/V2ConditionEditorDialog';
import { TopControlPanel } from '@modules/reactFlow/components/shared/TopControlPanel';
import { StrategyCanvasEditor } from '@modules/reactFlow/components/shared/StrategyCanvasEditor';
import type { StepNode, RouteGuard, FunnelVariable, FlowDocument, ScreenTransition } from '@segmently/schema-models/v2';
import type { Strategy } from '@segmently/schema-models/strategy';
import { flowVariablesService } from '@modules/firebase-core/services/FlowVariablesService';
import type { Edge } from '@xyflow/react';
import { V2ScreenFlowCoordinator } from '@modules/reactFlow/services/onboarding/V2ScreenFlowCoordinator';
import {
  isFlowVariablesHost,
  readFlowAnimationDefaults,
  reloadFlowAnimationDefaults,
  type FlowVariablesHost,
} from '@modules/reactFlow/services/shared/FlowCoordinator';
import { v2ScreenRegistry } from '@modules/reactFlow/services/onboarding/V2ScreenRegistry';
import { v2TransitionManager } from '@modules/reactFlow/services/onboarding/V2TransitionManager';
import { v2CanvasClipboardService } from '@modules/reactFlow/services/onboarding/V2CanvasClipboardService';
import { v2StepPasteProcessor } from '@modules/reactFlow/services/onboarding/V2StepPasteProcessor';
import { v2ClipboardVariablePortabilityService } from '@modules/reactFlow/services/onboarding/V2ClipboardVariablePortabilityService';
import { PasteV2EntitiesCommand } from '@modules/reactFlow/commands/PasteV2EntitiesCommand';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import { useCanvasEditorState } from '@modules/reactFlow/components/shared/hooks/useCanvasEditorState';
import { useCanvasEventHandlers } from '@modules/reactFlow/components/shared/hooks/useCanvasEventHandlers';
import type { EventHandlerContext, EdgeDialogState } from '@modules/reactFlow/components/shared/hooks/useCanvasEventHandlers';
import { CanvasLocaleContext } from '@modules/reactFlow/contexts/CanvasLocaleContext';
import { CanvasVariablesContext } from '@modules/reactFlow/contexts/CanvasVariablesContext';
import { CanvasBadgesContext } from '@modules/reactFlow/contexts/CanvasBadgesContext';
import { buildPreviewVariables } from '@modules/v2-variable-utils';
import CanvasScreenSearch from './CanvasScreenSearch';
import CanvasLocaleSelector from './CanvasLocaleSelector';
import { OnboardingBlockTagControls, type AssignBlockTagPayload } from './OnboardingBlockTagControls';
import { isKeyboardCapturedByOverlay } from '@modules/reactFlow/utils/keyboardGuard';
import { FontLabelsProvider } from '@modules/font-config/context/FontLabelsContext';
import { FontConfigFromProjectProvider } from '@modules/font-config/context/FontConfigFromProjectProvider';
import { StrategyExportService } from '@modules/strategy-blocks/services/StrategyExportService';
import { StrategyImportDialog } from '@modules/strategy-blocks/components/StrategyImportDialog';
import { StrategyCanvasActions } from '@modules/strategy-blocks/components/StrategyCanvasActions';
import { StrategySyncConfirmDialog } from '@modules/strategy-blocks/components/StrategySyncConfirmDialog';
import { AgentApiClient } from '@modules/content-agent/services/AgentApiClient';
import type { CliStrategyBlockPullData, CliStrategyPushData, CliStrategySyncResolutions } from '@modules/shared-types';
import type { ImportResult } from '@modules/strategy-blocks/services/StrategyImportService';
import {
  applyStrategyScreenTransplantPastePolicy,
  extractStrategyIdFromStrategyBlocksMetadata,
  prepareStrategyScreenTransplant,
} from '@modules/strategy-blocks/utils/strategyScreenTransplant';
import type { StrategyFlowScreen } from '@modules/strategy-blocks/utils/strategyFlowDocumentTransformer';
import {
  STRATEGY_BLOCK_DOC_TAG_PREFIX,
  STRATEGY_BLOCKS_MANIFEST_METADATA_KEY,
  STRATEGY_BLOCK_TAG_PREFIX,
  STRATEGY_TAG_PREFIX,
} from '@modules/strategy-blocks/utils/strategyFlowDocumentTransformer';
import {
  StrategyThemeSelectionDialog,
  getThemeSelectionOptionKey,
  resolveCurrentThemeOptionKey,
  type StrategyThemeSelectionAction,
  type StrategyThemeSelectionOption,
} from '@modules/themes/components/StrategyThemeSelectionDialog';
import type { ThemeV2 } from '@modules/themes/types/themesV2.types';
import {
  buildFunnelOverlayMaps,
  FUNNEL_INSIGHTS_ACCESS_CAPABILITY,
  FunnelInsightsEdgeDrawer,
  extractPaywallProductLabels,
  useFunnelEdgeDrilldown,
  useFunnelOverlay,
  useTraffickedVersions,
  AnalyticsHonestyBanner,
  AnalyticsNodeDrawer,
  ReportModeBar,
  ReportFilterPanel,
  buildAppliedFilterChips,
  countAppliedFilterClauses,
  DropoffAudienceDrawer,
  useFlowLossDrilldown,
  ANALYTICS_CANVAS_TEST_IDS,
  FUNNEL_UNIT_URL_PARAM,
  FunnelUnitProvider,
  applyFunnelUnitParam,
  parseFunnelUnitParam,
  type FunnelInsightEdgeMetric,
  type FunnelInsightFilters,
  type FunnelInsightNodeMetric,
  type FunnelUnit,
} from '@modules/funnel-insights';
import { ReadOnlyFunnelCanvas } from '@modules/reactFlow/components/shared/ReadOnlyFunnelCanvas';

const CANVAS_OVERVIEW_ANCHOR = GUIDE_ANCHORS.canvasOverview;

type StrategyApplyScreenRuntimeFields = {
  __strategyBlockId?: string;
  __strategyBlockName?: string;
  __strategyScreenId?: string;
};

function stripStrategyApplyRuntimeFields<T extends Record<string, unknown>>(screen: T): T {
  const {
    __strategyBlockId: _strategyBlockId,
    __strategyBlockName: _strategyBlockName,
    __strategyScreenId: _strategyScreenId,
    ...clean
  } = screen as T & StrategyApplyScreenRuntimeFields;
  return clean as T;
}

export interface OnboardingCanvasEditorV2Handle {
  openStrategyAssociationDialog: () => void;
  openAssociatedStrategy: () => void;
  /** Actions → Change theme… (admin): opens the onboarding theme picker. No-op on a strategy canvas. */
  openChangeTheme: () => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function parseStrategyBlocksManifest(value: unknown): Record<string, unknown> {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return isRecord(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return isRecord(value) ? value : {};
}

export interface OnboardingCanvasEditorV2Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onboardingId: string;
  versionId: string;
  onboardingName?: string;
  versionName?: string;
  /** If true, render canvas directly without Portal wrapper */
  embedded?: boolean;
  /** If true, hide the top control panel */
  hideTopPanel?: boolean;
  /** Custom actions to render in the top control panel */
  customActions?: React.ReactNode;
  /** Imperative bridge used by the route-level Actions menu. */
  controlRef?: React.MutableRefObject<OnboardingCanvasEditorV2Handle | null>;
  /** Called when this canvas updates the onboarding-version strategy association. */
  onStrategyAssociationChanged?: (strategyId: string | null) => void;
  /** Strategy association resolved by the route-level wrapper. */
  associatedStrategyId?: string | null;
  /** Screen ID to auto-open editor for after canvas loads */
  autoOpenScreenId?: string;
  /** Callback when a screen editor is opened from the canvas */
  onScreenEditorOpen?: (screenId: string) => void;
  /** Callback when the screen editor is closed */
  onScreenEditorClose?: () => void;
  /** Default language from V2 flow document */
  defaultLanguage?: string;
  /** Available languages from V2 flow document */
  languages?: string[];
  /** Callback when syncing state changes */
  onSyncingChange?: (isSyncing: boolean) => void;
  /** Coordinator factory — replaces hardcoded V2ScreenFlowCoordinator creation.
   * When provided, onboardingId/versionId may be empty (strategy mode).
   * Must return a FlowCoordinator (article and strategy coordinators do NOT
   * share V2ScreenFlowCoordinator's interface): onboarding-only members are
   * optional capabilities read through their helpers (isFlowVariablesHost,
   * readFlowAnimationDefaults). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  coordinatorFactory?: () => any;
  /**
   * Host discriminator for host-specific UI (block tags, strategy association,
   * funnel insights, screen picker are onboarding-only). Defaults to
   * 'strategy' when strategyId is set, else 'onboarding' — existing callers
   * keep today's behavior without passing anything.
   */
  hostKind?: 'onboarding' | 'strategy' | 'article';
  /**
   * External template list forwarded to the template panel (panel prop already
   * exists; e.g. the article host passes FlexibleLayout-only presets).
   * Omitted → panel keeps its theme/default template sources.
   */
  templates?: StepNode[];
  /** Editor mode for screen editor dialog — 'full' (default) or 'ai' */
  editorMode?: 'full' | 'ai';
  /** External flow variables (e.g. strategy.variables) — skips flowVariablesService loading */
  externalVariables?: Record<string, FunnelVariable>;
  /** Hide locale selector + search (strategy screens are single-language) */
  hideLocaleTools?: boolean;
  /** Strategy ID — when provided, loads theme from strategy subcollection instead of onboarding */
  strategyId?: string;
  /** Block ID — for block-level export/import (strategy mode only) */
  blockId?: string;
  /** Strategy block entity ID — used for source provenance when copying screens from a block canvas */
  strategyBlockEntityId?: string;
  /**
   * Target platform of the onboarding (from `Onboarding.platform`). Forwarded
   * to the V2 screen editor dialog so that web onboardings open on the Safari
   * Preview device by default. Undefined in strategy mode and for legacy
   * onboardings without a platform — in those cases the dialog falls back to
   * the plain iPhone mockup.
   */
  platform?: 'ios' | 'android' | 'web';
  /**
   * True when the selected onboarding version is published/frozen (Spec A / D51,
   * A4-3). Renders a read-only banner; editing should mint a new version first.
   */
  frozen?: boolean;
  /** Banner CTA — mint a new editable version from the frozen one. */
  onCreateEditableVersion?: () => void;
  /**
   * R7/P3 (no-dead-end): a screen-editor SAVE on a frozen version retargets here —
   * the owner mints a new version carrying this payload instead of losing the work.
   */
  onSaveToNewVersion?: (step: FlowStepNode) => Promise<void>;
  /**
   * U13 (Spec D2, AC-D2-8): viewing a NON-HEAD version — same edit-disable path
   * as `frozen` (saves retarget to a new version) with "viewing vN" banner copy.
   */
  viewingOnly?: boolean;
  /** Version name for the U13 banner copy. */
  viewingOnlyVersionName?: string;
  /** U13 banner CTA — switch the editor back to the head (latest) version. */
  onBackToLatest?: () => void;
  /**
   * I4 (analytics-versions-and-publish-flow): report-mode version switcher —
   * the honesty-banner chip and the zero-traffic warning call this with the
   * target versionId (the host routes it through the editor's switchVersion).
   */
  onSwitchVersion?: (versionId: string) => void;
}

export const OnboardingCanvasEditorV2: React.FC<OnboardingCanvasEditorV2Props> = ({
  open,
  onClose,
  projectId,
  onboardingId,
  versionId,
  onboardingName,
  versionName,
  embedded = false,
  hideTopPanel = false,
  customActions,
  controlRef,
  onStrategyAssociationChanged,
  associatedStrategyId,
  autoOpenScreenId,
  onScreenEditorOpen,
  onScreenEditorClose,
  defaultLanguage,
  languages,
  onSyncingChange,
  coordinatorFactory,
  hostKind,
  templates,
  editorMode,
  externalVariables,
  hideLocaleTools,
  strategyId,
  blockId: strategyBlockId,
  strategyBlockEntityId,
  platform,
  frozen = false,
  onCreateEditableVersion,
  onSaveToNewVersion,
  viewingOnly = false,
  viewingOnlyVersionName,
  onBackToLatest,
  onSwitchVersion,
}) => {
  const { isAdmin } = useAuth() as { isAdmin?: boolean };
  // Host discriminator: existing callers pass no hostKind, so the default
  // reproduces today's implicit strategy/onboarding split exactly.
  const effectiveHostKind: 'onboarding' | 'strategy' | 'article' =
    hostKind ?? (strategyId ? 'strategy' : 'onboarding');
  const isOnboardingHost = effectiveHostKind === 'onboarding';
  const guideService = useMemo(() => new GuideService(), []);
  const onboardingService = useMemo(() => new OnboardingService(), []);
  const strategyService = useMemo(() => new StrategyService(projectId), [projectId]);
  const projectThemeService = useMemo(() => new ProjectThemeService(), []);
  const globalThemeService = useMemo(() => new ThemeV2Service(), []);

  // Compute effective locale values with fallbacks
  const effectiveDefaultLanguage = defaultLanguage || 'en-US';
  const effectiveLanguages = languages || ['en-US'];

  // Load theme settings for this onboarding (used for themed defaults on element toggle)
  // Skip in strategy mode (no onboardingId)
  const {
    themeSettings: onboardingThemeSettings,
    refresh: refreshOnboardingThemeSettings,
    update: updateOnboardingThemeSettings,
  } = useOnboardingThemeSettingsV2({
    projectId,
    onboardingId: onboardingId || '',
    skipInitialLoad: !!strategyId || !onboardingId,
  });

  // Load strategy theme settings (when in strategy mode)
  const { themeSettings: strategyThemeSettings, update: updateStrategyTheme } = useStrategyThemeSettings({
    projectId,
    strategyId: strategyId || '',
    skipInitialLoad: !strategyId,
  });

  // Use strategy theme if available, otherwise onboarding theme
  const themeSettingsV2 = strategyId ? strategyThemeSettings : onboardingThemeSettings;

  // Strategy theme editor state (strategy mode only)
  const [themeReplaceOpen, setThemeReplaceOpen] = useState(false);
  // Declared up here: the onboarding theme switch (below) refreshes the template panel through it.
  const templatePanelRef = useRef<OnboardingTemplatePanelV2Handle>(null);
  const [themeReplaceLoading, setThemeReplaceLoading] = useState(false);
  const [themeReplaceError, setThemeReplaceError] = useState<string | null>(null);
  const [themeReplaceOptions, setThemeReplaceOptions] = useState<StrategyThemeSelectionOption[]>([]);
  const [selectedThemeReplaceOption, setSelectedThemeReplaceOption] = useState<StrategyThemeSelectionOption | null>(null);
  // Onboarding theme switch (admin): the picker reuses the state above in its `onboarding` mode.
  const [themeReplaceCurrentKey, setThemeReplaceCurrentKey] = useState<string | null>(null);
  const [themeChangedName, setThemeChangedName] = useState<string | null>(null);
  // One switch at a time: a double click must not send a second POST before React re-renders.
  const themeChangeInFlightRef = useRef(false);

  // Click-to-add screen picker (Cmd/Ctrl+K)
  const [screenPickerOpen, setScreenPickerOpen] = useState(false);

  // Selective theme apply (Surface A: theme editor by types, Surface B: bulk actions by instances)
  const [applyThemeDialog, setApplyThemeDialog] = useState<{
    theme: ThemeV2;
    screens: FlowStepNode[];
    sourceLabel: string;
    scopeLabel: string;
    allowThemeEdit?: boolean;
    initialGroups?: ThemeApplyGroup[];
    /** Per-kind template id preference (the clicked tile) for duplicate-kind themes. */
    templateIdByKind?: Record<string, string>;
  } | null>(null);
  const [applyThemeSummary, setApplyThemeSummary] = useState<string | null>(null);
  const [targetStrategyIdForPaste, setTargetStrategyIdForPaste] = useState<string | null>(associatedStrategyId ?? null);
  const [strategyAssociationDialogOpen, setStrategyAssociationDialogOpen] = useState(false);
  const [strategyAssociationSaving, setStrategyAssociationSaving] = useState(false);
  const [strategyAssociationError, setStrategyAssociationError] = useState<string | null>(null);
  const [strategyListLoading, setStrategyListLoading] = useState(false);
  const [availableStrategies, setAvailableStrategies] = useState<Strategy[]>([]);
  const [selectedStrategyForAssociation, setSelectedStrategyForAssociation] = useState<Strategy | null>(null);
  const [associatedStrategy, setAssociatedStrategy] = useState<Strategy | null>(null);
  const [associatedStrategyOverlayOpen, setAssociatedStrategyOverlayOpen] = useState(false);

  // `wizardOrder`: system themes in the create wizard's order (`order`, then newest) — the onboarding
  // picker; the strategy picker keeps its own (latest update first).
  const loadStrategyThemeReplaceOptions = useCallback(async (
    wizardOrder = false,
  ): Promise<StrategyThemeSelectionOption[]> => {
    const [projectThemes, listedGlobalThemes] = await Promise.all([
      projectThemeService.list(projectId),
      globalThemeService.list({ limit: 100 }),
    ]);
    const globalThemes = wizardOrder ? [...listedGlobalThemes].sort(compareThemesV2ByOrder) : listedGlobalThemes;

    return [
      ...projectThemes.map((theme): StrategyThemeSelectionOption => ({
        id: theme.id,
        name: theme.name,
        source: 'project',
        isActive: theme.isActive,
        settings: cloneOnboardingThemeSettings(theme, {
          sourceThemeId: theme.sourceThemeId ?? theme.id,
        }),
      })),
      ...globalThemes.map((theme): StrategyThemeSelectionOption => ({
        id: theme.id,
        name: theme.name,
        source: 'global',
        settings: themeV2ToOnboardingSettings(theme, {
          sourceThemeId: theme.id,
        }),
      })),
    ];
  }, [globalThemeService, projectId, projectThemeService]);

  const loadAllStrategyScreensForThemeApply = useCallback(async (): Promise<FlowStepNode[]> => {
    if (!strategyId) return [];
    const targets = await strategyService.listAllStepNodeScreens(strategyId);
    return targets.map(({ blockId, blockName, screenId, screen }) => ({
      ...(screen as any),
      entityId: (screen as any).entityId ?? screen.id,
      __strategyBlockId: blockId,
      __strategyBlockName: blockName,
      __strategyScreenId: screenId,
    } as FlowStepNode & StrategyApplyScreenRuntimeFields));
  }, [strategyId, strategyService]);

  const handleOpenReplaceTheme = useCallback(async () => {
    if (!strategyId) return;
    setThemeReplaceOpen(true);
    setThemeReplaceLoading(true);
    setThemeReplaceError(null);
    setThemeReplaceOptions([]);
    setSelectedThemeReplaceOption(null);
    try {
      const options = await loadStrategyThemeReplaceOptions(false);
      setThemeReplaceOptions(options);
      setSelectedThemeReplaceOption(options.find(option => option.source === 'project' && option.isActive) ?? options[0] ?? null);
    } catch (err) {
      setThemeReplaceError(err instanceof Error ? err.message : 'Failed to load themes');
    } finally {
      setThemeReplaceLoading(false);
    }
  }, [loadStrategyThemeReplaceOptions, strategyId]);

  const handleApplyCurrentStrategyThemeToAllScreens = useCallback(async () => {
    if (!strategyId || !strategyThemeSettings) return;
    const targets = await loadAllStrategyScreensForThemeApply();
    if (targets.length === 0) {
      setApplyThemeSummary('No strategy screens available for theme apply');
      return;
    }
    setApplyThemeDialog({
      theme: onboardingSettingsV2ToThemeV2(strategyThemeSettings, {
        id: strategyId,
        name: 'Current strategy theme',
      }),
      screens: targets,
      sourceLabel: 'Current strategy theme',
      scopeLabel: `All strategy screens (${targets.length})`,
    });
  }, [loadAllStrategyScreensForThemeApply, strategyId, strategyThemeSettings]);

  const handleConfirmReplaceTheme = useCallback(async (action: StrategyThemeSelectionAction) => {
    if (!strategyId || !selectedThemeReplaceOption) return;
    setThemeReplaceLoading(true);
    setThemeReplaceError(null);
    try {
      const settings = cloneOnboardingThemeSettings(selectedThemeReplaceOption.settings, {
        sourceThemeId: selectedThemeReplaceOption.settings.sourceThemeId ?? selectedThemeReplaceOption.id,
      });
      await updateStrategyTheme(settings);

      if (action === 'add-and-apply') {
        const targets = await loadAllStrategyScreensForThemeApply();
        if (targets.length === 0) {
          setApplyThemeSummary('Strategy theme updated · no screens available for theme apply');
          return;
        }
        setApplyThemeDialog({
          theme: onboardingSettingsV2ToThemeV2(settings, {
            id: selectedThemeReplaceOption.id,
            name: selectedThemeReplaceOption.name,
          }),
          screens: targets,
          sourceLabel: `${selectedThemeReplaceOption.name} (${selectedThemeReplaceOption.source})`,
          scopeLabel: `All strategy screens (${targets.length})`,
          initialGroups: [...ALL_THEME_APPLY_GROUPS] as ThemeApplyGroup[],
        });
        setThemeReplaceOpen(false);
      } else {
        setApplyThemeSummary(`Strategy theme updated with ${selectedThemeReplaceOption.name}`);
        setThemeReplaceOpen(false);
      }
    } catch (err) {
      setThemeReplaceError(err instanceof Error ? err.message : 'Failed to replace strategy theme');
    } finally {
      setThemeReplaceLoading(false);
    }
  }, [
    loadAllStrategyScreensForThemeApply,
    selectedThemeReplaceOption,
    strategyId,
    updateStrategyTheme,
  ]);

  // Onboarding theme switch (admin; plan 2026-10-05-onboarding-theme-switch D4). The same list as the
  // create wizard: product themes, then system themes in the wizard's order. The onboarding's recorded
  // source is marked "Current" and preselected (read fresh at the action boundary).
  const openChangeOnboardingTheme = useCallback(async () => {
    if (strategyId || !onboardingId) return;
    setThemeReplaceOpen(true);
    setThemeReplaceLoading(true);
    setThemeReplaceError(null);
    setThemeReplaceOptions([]);
    setSelectedThemeReplaceOption(null);
    setThemeReplaceCurrentKey(null);
    try {
      const [options, onboarding] = await Promise.all([
        loadStrategyThemeReplaceOptions(true),
        onboardingService.getOnboarding(projectId, onboardingId),
      ]);
      const currentKey = resolveCurrentThemeOptionKey(options, onboarding?.sourceThemeId, onboarding?.sourceThemeKind);
      setThemeReplaceOptions(options);
      setThemeReplaceCurrentKey(currentKey);
      setSelectedThemeReplaceOption(options.find((option) => getThemeSelectionOptionKey(option) === currentKey) ?? null);
    } catch (err) {
      setThemeReplaceError(err instanceof Error ? err.message : 'Failed to load themes');
    } finally {
      setThemeReplaceLoading(false);
    }
  }, [loadStrategyThemeReplaceOptions, onboardingId, onboardingService, projectId, strategyId]);

  // The switch runs on the server (the same route as `themes set-onboarding`): it replaces the theme and
  // its screen templates only. Both theme hook instances (this hub, the template panel) re-read it, so
  // new screens and the gear editor use the new theme at once; canvas screens keep their look.
  const handleConfirmChangeOnboardingTheme = useCallback(async () => {
    if (!onboardingId || !selectedThemeReplaceOption || themeChangeInFlightRef.current) return;
    themeChangeInFlightRef.current = true;
    setThemeReplaceLoading(true);
    setThemeReplaceError(null);
    try {
      const receipt = await OnboardingThemeApi.switchTheme(projectId, onboardingId, {
        source: selectedThemeReplaceOption.source === 'global' ? 'system' : 'project',
        themeId: selectedThemeReplaceOption.id,
      });
      await Promise.all([
        refreshOnboardingThemeSettings(),
        templatePanelRef.current?.refreshThemeSettings(),
      ]);
      setThemeReplaceOpen(false);
      setThemeChangedName(receipt.themeName);
    } catch (err) {
      setThemeReplaceError(err instanceof Error ? err.message : 'Failed to change the theme');
    } finally {
      themeChangeInFlightRef.current = false;
      setThemeReplaceLoading(false);
    }
  }, [onboardingId, projectId, refreshOnboardingThemeSettings, selectedThemeReplaceOption]);

  useEffect(() => {
    if (strategyId || associatedStrategyId === undefined) return;
    setTargetStrategyIdForPaste(associatedStrategyId || null);
  }, [associatedStrategyId, strategyId]);

  useEffect(() => {
    if (!open || strategyId || !onboardingId || !versionId) {
      setTargetStrategyIdForPaste(null);
      return;
    }

    let isActive = true;
    onboardingService.getVersion(projectId, onboardingId, versionId)
      .then((version) => {
        if (!isActive) return;
        const resolved = extractStrategyIdFromStrategyBlocksMetadata(version?.screensGraph?.metadata) ?? null;
        setTargetStrategyIdForPaste(resolved);
        onStrategyAssociationChanged?.(resolved);
      })
      .catch((err) => {
        console.warn('[CanvasEditorV2] Failed to load strategy metadata for paste policy:', err);
        if (isActive) {
          setTargetStrategyIdForPaste(null);
          onStrategyAssociationChanged?.(null);
        }
      });

    return () => {
      isActive = false;
    };
  }, [onStrategyAssociationChanged, onboardingId, onboardingService, open, projectId, strategyId, versionId]);

  useEffect(() => {
    if (!open || !targetStrategyIdForPaste) {
      setAssociatedStrategy(null);
      return;
    }

    let isActive = true;
    strategyService.get(targetStrategyIdForPaste)
      .then((strategy) => {
        if (!isActive) return;
        setAssociatedStrategy(strategy);
      })
      .catch((err) => {
        console.warn('[CanvasEditorV2] Failed to load associated strategy:', err);
        if (isActive) setAssociatedStrategy(null);
      });

    return () => {
      isActive = false;
    };
  }, [open, strategyService, targetStrategyIdForPaste]);

  const openStrategyAssociationDialog = useCallback(() => {
    if (strategyId) return;
    setStrategyAssociationError(null);
    setSelectedStrategyForAssociation(associatedStrategy);
    setStrategyAssociationDialogOpen(true);
  }, [associatedStrategy, strategyId]);

  const openAssociatedStrategy = useCallback(() => {
    const strategyToOpen = targetStrategyIdForPaste || associatedStrategyId || null;
    if (!strategyToOpen) return;
    setTargetStrategyIdForPaste(strategyToOpen);
    setAssociatedStrategyOverlayOpen(true);
  }, [associatedStrategyId, targetStrategyIdForPaste]);

  useEffect(() => {
    if (!strategyAssociationDialogOpen || !projectId || strategyId) return;

    let isActive = true;
    setStrategyListLoading(true);
    strategyService.list({ limit: 100 })
      .then((strategies) => {
        if (!isActive) return;
        setAvailableStrategies(strategies);
        const current = strategies.find((strategy) => strategy.id === targetStrategyIdForPaste)
          ?? associatedStrategy
          ?? null;
        setSelectedStrategyForAssociation(current);
      })
      .catch((err) => {
        console.warn('[CanvasEditorV2] Failed to load strategies for association:', err);
        if (isActive) {
          setAvailableStrategies([]);
          setStrategyAssociationError(err instanceof Error ? err.message : 'Failed to load strategies');
        }
      })
      .finally(() => {
        if (isActive) setStrategyListLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [
    associatedStrategy,
    projectId,
    strategyAssociationDialogOpen,
    strategyId,
    strategyService,
    targetStrategyIdForPaste,
  ]);

  useEffect(() => {
    registryUpdatesSuspendedRef.current = !strategyId && associatedStrategyOverlayOpen;
  }, [associatedStrategyOverlayOpen, strategyId]);

  const handleStrategyAssociationSave = useCallback(async () => {
    if (!selectedStrategyForAssociation || !onboardingId || !versionId || strategyAssociationSaving) return;

    setStrategyAssociationSaving(true);
    setStrategyAssociationError(null);

    try {
      const version = await onboardingService.getVersion(projectId, onboardingId, versionId);
      if (!version) {
        throw new Error('Onboarding version not found');
      }

      const screensGraph = version.screensGraph ?? {};
      const metadata = isRecord(screensGraph.metadata) ? screensGraph.metadata : {};
      const existingManifest = parseStrategyBlocksManifest(metadata[STRATEGY_BLOCKS_MANIFEST_METADATA_KEY]);
      const nextManifest = {
        ...existingManifest,
        strategyId: selectedStrategyForAssociation.id,
        strategyName: selectedStrategyForAssociation.name || selectedStrategyForAssociation.id,
        associationSource: 'manual',
        associatedAt: new Date().toISOString(),
      };

      await onboardingService.updateVersion(projectId, onboardingId, versionId, {
        screensGraph: {
          ...screensGraph,
          metadata: {
            ...metadata,
            [STRATEGY_BLOCKS_MANIFEST_METADATA_KEY]: nextManifest,
          },
        },
      });

      setTargetStrategyIdForPaste(selectedStrategyForAssociation.id);
      setAssociatedStrategy(selectedStrategyForAssociation);
      onStrategyAssociationChanged?.(selectedStrategyForAssociation.id);
      setStrategyAssociationDialogOpen(false);

      // Spec D4 (U9/G3, USER decision 3): right after linking, sync the theme
      // onboarding→strategy so the strategy SSoT matches what is live — then
      // compile's default `strategy-current` theme is correct by construction.
      // Non-fatal: the association above already succeeded.
      try {
        const { getAuth } = await import('firebase/auth');
        const token = await getAuth().currentUser?.getIdToken();
        if (!token) throw new Error('User not authenticated');
        const baseUrl = import.meta.env.VITE_BUILDER_API_URL
          || import.meta.env.VITE_AGENT_API_URL
          || 'http://localhost:8081';
        const response = await fetch(
          `${baseUrl}/api/cli/v1/projects/${encodeURIComponent(projectId)}/funnels/${encodeURIComponent(onboardingId)}/sync-strategy-theme`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ direction: 'onboarding-to-strategy', strategyId: selectedStrategyForAssociation.id }),
          },
        );
        if (!response.ok) {
          const payload = await response.json().catch(() => undefined);
          throw new Error(payload?.detail || payload?.error?.message || `HTTP ${response.status}`);
        }
      } catch (syncError) {
        setPasteWarning(
          'Strategy linked, but the theme sync failed — the strategy theme may differ from this onboarding. '
            + `Retry via strategy canvas → Advanced… → theme tools. (${syncError instanceof Error ? syncError.message : 'unknown error'})`,
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to associate strategy';
      setStrategyAssociationError(message);
    } finally {
      setStrategyAssociationSaving(false);
    }
  }, [
    onboardingId,
    onboardingService,
    onStrategyAssociationChanged,
    projectId,
    selectedStrategyForAssociation,
    strategyAssociationSaving,
    versionId,
  ]);

  useEffect(() => {
    if (!controlRef) return;
    controlRef.current = {
      openStrategyAssociationDialog,
      openAssociatedStrategy,
      openChangeTheme: () => { void openChangeOnboardingTheme(); },
    };

    return () => {
      if (controlRef.current?.openStrategyAssociationDialog === openStrategyAssociationDialog) {
        controlRef.current = null;
      }
    };
  }, [controlRef, openAssociatedStrategy, openChangeOnboardingTheme, openStrategyAssociationDialog]);

  const resolveTargetStrategyIdForPaste = useCallback(async (): Promise<string | null> => {
    if (strategyId || !onboardingId || !versionId) {
      return null;
    }
    if (targetStrategyIdForPaste) {
      return targetStrategyIdForPaste;
    }

    try {
      const version = await onboardingService.getVersion(projectId, onboardingId, versionId);
      const resolved = extractStrategyIdFromStrategyBlocksMetadata(version?.screensGraph?.metadata) ?? null;
      setTargetStrategyIdForPaste(resolved);
      return resolved;
    } catch (err) {
      console.warn('[CanvasEditorV2] Failed to resolve strategy metadata for paste policy:', err);
      return null;
    }
  }, [onboardingId, onboardingService, projectId, strategyId, targetStrategyIdForPaste, versionId]);

  // Block-level export/import (strategy mode only)
  const [blockImportOpen, setBlockImportOpen] = useState(false);

  const handleExportBlock = useCallback(async () => {
    if (!strategyId || !strategyBlockId) return;
    try {
      const exportService = new StrategyExportService();
      const envelope = await exportService.exportBlockPayload(projectId, strategyId, strategyBlockId);
      const name = envelope.metadata.blockName?.replace(/\s+/g, '_') || strategyBlockId;
      StrategyExportService.download(envelope, `block_${name}_${Date.now()}.json`);
    } catch (err) {
      console.error('[CanvasEditorV2] Block export failed:', err);
    }
  }, [projectId, strategyId, strategyBlockId]);

  const handleBlockImported = useCallback((_result: ImportResult) => {
    // Reload canvas to show imported screens
    window.location.reload();
  }, []);

  // Per-block pull/push against the linked onboarding (strategy mode only)
  const [syncDialogMode, setSyncDialogMode] = useState<'pull-block' | 'push-block' | null>(null);
  const [syncDryRun, setSyncDryRun] = useState<CliStrategyBlockPullData | CliStrategyPushData | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const handleOpenBlockSyncDialog = useCallback(async (mode: 'pull-block' | 'push-block') => {
    if (!strategyId || !strategyBlockId) return;
    setSyncDialogMode(mode);
    setSyncDryRun(null);
    setSyncError(null);
    setSyncLoading(true);
    try {
      const api = new AgentApiClient();
      const response = mode === 'pull-block'
        ? await api.pullStrategyBlock({ projectId, strategyId, blockId: strategyBlockId, dryRun: true })
        : await api.pushStrategyToOnboarding({ projectId, strategyId, blockIds: [strategyBlockId], dryRun: true });
      if (!response.success) {
        throw new Error(response.error || 'Sync dry-run failed');
      }
      setSyncDryRun(response as unknown as CliStrategyBlockPullData | CliStrategyPushData);
    } catch (err) {
      console.error('[CanvasEditorV2] Block sync dry-run failed:', err);
      setSyncError((err as Error).message);
    } finally {
      setSyncLoading(false);
    }
  }, [projectId, strategyId, strategyBlockId]);

  const handleConfirmBlockSync = useCallback(async (resolutions: CliStrategySyncResolutions) => {
    if (!strategyId || !strategyBlockId || !syncDialogMode) return;
    setSyncLoading(true);
    setSyncError(null);
    try {
      const api = new AgentApiClient();
      const response = syncDialogMode === 'pull-block'
        ? await api.pullStrategyBlock({ projectId, strategyId, blockId: strategyBlockId, resolutions })
        : await api.pushStrategyToOnboarding({ projectId, strategyId, blockIds: [strategyBlockId], resolutions });
      if (!response.success) {
        throw new Error(response.error || 'Sync failed');
      }
      setSyncDialogMode(null);
      if (syncDialogMode === 'pull-block') {
        // Reload the block screens canvas to show pulled screens (same path as block import).
        window.location.reload();
      }
    } catch (err) {
      console.error('[CanvasEditorV2] Block sync failed:', err);
      setSyncError((err as Error).message);
    } finally {
      setSyncLoading(false);
    }
  }, [projectId, strategyId, strategyBlockId, syncDialogMode]);

  const [insightsOverlayEnabled, setInsightsOverlayEnabled] = useState(false);
  const [insightsFilters, setInsightsFilters] = useState<FunnelInsightFilters>({});
  const [selectedInsightsEdge, setSelectedInsightsEdge] = useState<FunnelInsightEdgeMetric | null>(null);
  // Spec D D1b: analytics mode swaps the editor canvas for a read-only canvas
  // fed a SNAPSHOT of the live graph (taken while the editor is still mounted).
  const [analyticsSnapshot, setAnalyticsSnapshot] = useState<{
    nodes: Array<Record<string, unknown>>;
    edges: Array<Record<string, unknown>>;
  } | null>(null);
  const [selectedInsightsNode, setSelectedInsightsNode] = useState<{
    metric: FunnelInsightNodeMetric;
    screenName?: string;
    screenType?: string;
  } | null>(null);
  // I-A: flow-loss stub click → «who dropped at this step» drawer (P-3 on-demand).
  const [flowLossScreenId, setFlowLossScreenId] = useState<string | null>(null);
  const insightsAccess = useProjectCapability(projectId, FUNNEL_INSIGHTS_ACCESS_CAPABILITY, { adminBypass: false });
  const canUseInsightsOverlay = isOnboardingHost && insightsAccess.allowed;
  // Reports parity: the dashboard Open action deep-links `?insights=1&from=…&to=…`
  // so the canvas report covers the SAME period the user was reading. An
  // invalid/partial pair is ignored WHOLE (falls back to all-time) — a
  // half-applied window would silently misreport. Read once per mount, same
  // one-shot idiom as the insights deep-link below.
  const insightsWindow = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const from = params.get('from');
    const to = params.get('to');
    if (!from || !to) return null;
    const fromMs = Date.parse(from);
    const toMs = Date.parse(to);
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return null;
    return { from, to };
  }, []);
  // F2 (FT-AC-083, D2) + F4: the page counting unit. The URL param is the ONLY
  // carrier — a shared link reproduces the view, a fresh session (no param)
  // renders DEFAULT_FUNNEL_UNIT (people since the F4 flip), and no localStorage
  // preference exists. The param survives report-mode exit for re-entry, like
  // the window params above.
  const [funnelUnit, setFunnelUnit] = useState<FunnelUnit>(() => parseFunnelUnitParam(
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get(FUNNEL_UNIT_URL_PARAM)
      : null,
  ));
  const handleFunnelUnitChange = useCallback((unit: FunnelUnit) => {
    setFunnelUnit(unit);
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    // The default stays clean in the URL; the pair lives in funnelUnit.tsx so
    // this surface and the Reports hub can never disagree (F4).
    applyFunnelUnitParam(url.searchParams, unit);
    window.history.replaceState(window.history.state, '', url.toString());
  }, []);
  const funnelOverlay = useFunnelOverlay({
    projectId,
    onboardingId,
    versionId,
    enabled: insightsOverlayEnabled && canUseInsightsOverlay,
    filters: insightsFilters,
    from: insightsWindow?.from,
    to: insightsWindow?.to,
  });
  // I4: trafficked-versions list for the report-mode version switcher chip
  // and the zero-traffic landing warning (reuses the dashboard rollup, AC-028).
  const traffickedVersions = useTraffickedVersions({
    projectId,
    onboardingId,
    enabled: insightsOverlayEnabled && canUseInsightsOverlay,
    from: insightsWindow?.from,
    to: insightsWindow?.to,
  });
  const edgeDrilldown = useFunnelEdgeDrilldown({
    projectId,
    onboardingId,
    versionId,
    canvasTransitionId: selectedInsightsEdge?.canvasTransitionId,
    enabled: insightsOverlayEnabled && canUseInsightsOverlay && Boolean(selectedInsightsEdge),
    filters: insightsFilters,
  });
  // I-A: on-demand leaver audience for the clicked drain stub (v1 unfiltered —
  // the drawer says so when canvas filters are active).
  const flowLossDrilldown = useFlowLossDrilldown({
    projectId,
    onboardingId,
    versionId,
    dropScreenId: flowLossScreenId ?? undefined,
    enabled: insightsOverlayEnabled && canUseInsightsOverlay && Boolean(flowLossScreenId),
    from: insightsWindow?.from,
    to: insightsWindow?.to,
  });
  const handleFlowLossClick = useCallback((screenId: string) => {
    setFlowLossScreenId(screenId);
  }, []);

  const analyticsOverlay = useMemo(
    () => insightsOverlayEnabled && canUseInsightsOverlay ? buildFunnelOverlayMaps(funnelOverlay.data) : undefined,
    [canUseInsightsOverlay, funnelOverlay.data, insightsOverlayEnabled],
  );

  useEffect(() => {
    if (canUseInsightsOverlay || !insightsOverlayEnabled) return;
    setInsightsOverlayEnabled(false);
    setSelectedInsightsEdge(null);
    setSelectedInsightsNode(null);
    setAnalyticsSnapshot(null);
  }, [canUseInsightsOverlay, insightsOverlayEnabled]);

  const handleAnalyticsEdgeClick = useCallback((_edgeId: string, metric: FunnelInsightEdgeMetric) => {
    setSelectedInsightsEdge(metric);
  }, []);

  // Spec D D1b (AC-D1b-1): node click in analytics mode opens the metrics
  // drawer — NEVER the screen editor.
  const handleAnalyticsNodeClick = useCallback((screenId: string) => {
    const metric = analyticsOverlay?.nodesByScreenId[screenId];
    if (!metric) return;
    const step = v2ScreenRegistry.get(screenId);
    setSelectedInsightsNode({
      metric,
      screenName: typeof step?.name === 'string' ? step.name : screenId,
      screenType: step?.screenType,
    });
  }, [analyticsOverlay]);

  const analyticsModeActive = insightsOverlayEnabled && canUseInsightsOverlay && analyticsSnapshot !== null;

  // State management via shared hook
  const {
    canvasHandle,
    handleCanvasReady,
    selectedScreenIds,
    launchScreenId,
    setLaunchScreenId,
    isLoading,
    setIsLoading,
    isSyncing,
    setIsSyncing,
    setSyncOperation,
    commandManager,
    confirmDialog,
    setConfirmDialog,
    showConfirmation,
    settersRef,
  } = useCanvasEditorState('screens');

  // Report-mode enter/exit (P-10): ONE named pair — the toolbar toggle and the
  // report bar's Exit both go through these, so e2e helpers and future
  // callers share the exact sequence.
  const enterReportMode = useCallback(() => {
    // Snapshot the live graph BEFORE the editor canvas unmounts.
    setAnalyticsSnapshot(canvasHandle?.getGraphSnapshot() ?? { nodes: [], edges: [] });
    setInsightsOverlayEnabled(true);
    setSelectedInsightsEdge(null);
    setSelectedInsightsNode(null);
    setFlowLossScreenId(null);
  }, [canvasHandle]);
  const exitReportMode = useCallback(() => {
    // Leave analytics mode → editor canvas remounts and reloads from the
    // coordinator, exactly as before (AC-D1b-1/7). Drop `insights=1` so a
    // reload lands in the editor; the window params stay for re-entry.
    setInsightsOverlayEnabled(false);
    setAnalyticsSnapshot(null);
    setSelectedInsightsEdge(null);
    setSelectedInsightsNode(null);
    setFlowLossScreenId(null);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (url.searchParams.has('insights')) {
        url.searchParams.delete('insights');
        window.history.replaceState(window.history.state, '', url.toString());
      }
    }
  }, []);

  // Notify parent when syncing state changes
  useEffect(() => {
    onSyncingChange?.(isSyncing);
  }, [isSyncing, onSyncingChange]);

  // Coordinator state (V2ScreenFlowCoordinator — native V2)
  const [coordinator, setCoordinator] = useState<V2ScreenFlowCoordinator | null>(null);

  // Coordinators that own their variables storage (article drafts persist into
  // flowDocument.variables) expose the FlowVariablesHost capability — variable
  // load/save/delete then routes through the coordinator instead of
  // flowVariablesService. Onboarding/strategy coordinators don't expose it.
  const variablesHost = useMemo<FlowVariablesHost | null>(
    () => (coordinator && isFlowVariablesHost(coordinator) ? coordinator : null),
    [coordinator],
  );

  useEffect(() => {
    if (strategyId) return;

    if (associatedStrategyOverlayOpen) {
      associatedStrategyOverlayWasOpenRef.current = true;
      return;
    }

    if (!associatedStrategyOverlayWasOpenRef.current) return;
    associatedStrategyOverlayWasOpenRef.current = false;

    if (!open || !coordinator || !canvasHandle) return;

    let isActive = true;

    const restoreOnboardingRegistry = async () => {
      setIsLoading(true);
      try {
        await coordinator.loadScreensFromFirebase();
        if (!isActive) return;

        const launchId = await coordinator.getLaunchEntityId();
        if (!isActive) return;

        setLaunchScreenId(launchId || undefined);
        setRegistryVersion(v => v + 1);
        requestAnimationFrame(() => {
          if (!isActive) return;
          if (launchId) {
            canvasHandle.focusOnScreen(launchId, 0.35);
          } else {
            canvasHandle.fitView();
          }
        });
      } catch (err) {
        console.warn('[CanvasEditorV2] Failed to restore onboarding registry after strategy overlay:', err);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    void restoreOnboardingRegistry();

    return () => {
      isActive = false;
    };
  }, [
    associatedStrategyOverlayOpen,
    canvasHandle,
    coordinator,
    open,
    setIsLoading,
    setLaunchScreenId,
    strategyId,
  ]);

  // Dialog states
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [showEdgeDeleteConfirm, setShowEdgeDeleteConfirm] = useState(false);
  const [edgesToDelete, setEdgesToDelete] = useState<string[]>([]);

  // Screen editor dialog state (V1 Screen type for editor compatibility)
  const [screenEditorOpen, setScreenEditorOpen] = useState(false);
  const [selectedStepForEdit, setSelectedStepForEdit] = useState<FlowStepNode | null>(null);
  // Track the V2 entityId of the screen being edited (for save callback)
  const editingEntityIdRef = useRef<string | null>(null);

  // Condition editor state (V2 native — RouteGuard[] with logic connectors)
  const [conditionEditorOpen, setConditionEditorOpen] = useState(false);
  const [conditionEditorData, setConditionEditorData] = useState<{
    edgeId: string;
    sourceScreenId: string;
    targetScreenId: string;
    sourceStepName: string;
    targetStepName: string;
    currentConditions: RouteGuard[];
  } | null>(null);

  // Flow variables for V2 condition editor (async loaded from Firebase)
  const [flowVariables, setFlowVariables] = useState<Record<string, FunnelVariable>>({});
  const [pasteWarning, setPasteWarning] = useState<string | null>(null);

  // Registry version for triggering derived canvas recalculation
  const [registryVersion, setRegistryVersion] = useState(0);

  // Bumped after Apply→Animations rewrites screensGraph animation defaults so
  // the flowDocument memo re-reads them without reopening the canvas
  const [animationDefaultsVersion, setAnimationDefaultsVersion] = useState(0);

  // Template panel ref

  // Auto-open/focus tracking refs
  const autoOpenTriggeredRef = React.useRef(false);
  const lastAutoOpenScreenIdRef = React.useRef<string | undefined>(undefined);
  const autoFocusTriggeredRef = React.useRef(false);
  const registryUpdatesSuspendedRef = useRef(false);
  const associatedStrategyOverlayWasOpenRef = useRef(false);

  // Canvas toolbar state
  const [canvasLocale, setCanvasLocale] = useState<string>(effectiveDefaultLanguage);
  const [searchOpen, setSearchOpen] = useState(false);

  // K3 (D64 gap G3): sample values for @variable tokens in node previews —
  // defaultValue → isDefault/first option label → [Variable Name] chain.
  const canvasPreviewVariables = useMemo(
    () => ({ previewVariables: buildPreviewVariables(flowVariables, canvasLocale) }),
    [flowVariables, canvasLocale],
  );

  // Generation diagnostics (motivation-class chips) show ONLY on the strategy
  // screens canvas (editorMode="ai"); the regular onboarding canvas stays clean.
  const canvasBadges = useMemo(
    () => ({ showMotivationBadges: editorMode === 'ai' }),
    [editorMode],
  );

  // Debug logging
  useEffect(() => {
    if (open) {
      console.log('[CanvasEditorV2] Props received:', {
        projectId,
        onboardingId,
        versionId,
        onboardingName: onboardingName || 'Unnamed Onboarding',
        versionName,
      });
    }
  }, [open, projectId, onboardingId, versionId, onboardingName, versionName]);

  // Subscribe to V2 registry changes
  useEffect(() => {
    const unsubscribe = v2ScreenRegistry.subscribeGlobal(() => {
      if (registryUpdatesSuspendedRef.current) return;
      setRegistryVersion(v => v + 1);
    });
    return unsubscribe;
  }, []);

  // Event handler context
  const eventHandlerContext = useMemo<EventHandlerContext | null>(() => {
    if (!coordinator) return null;

    return {
      get setSelectedScreenIds() { return settersRef.current.setSelectedScreenIds; },
      get setIsSyncing() { return settersRef.current.setIsSyncing; },
      get setSyncOperation() { return settersRef.current.setSyncOperation; },
      coordinator: coordinator as any,
      commandManager,
      logEvent: () => {},
      showConfirmation,
    };
  }, [coordinator, commandManager, showConfirmation, settersRef]);

  // Edge dialog state for event handlers hook
  const edgeDialogState = useMemo<EdgeDialogState>(() => ({
    setEditorOpen,
    setSelectedEdge,
    setShowEdgeDeleteConfirm,
    setEdgesToDelete,
    edgesToDelete,
  }), [edgesToDelete]);

  // Event handlers via shared hook (uses V2 registry)
  const {
    handleScreensSelected,
    handleScreensMoved: handleScreensMovedBase,
    handleConnectionCompleted,
    handleScreenDropped,
    handleScreensDeleted,
    handleEdgesDeleted,
    confirmEdgeDeletion,
    handleEdgeClicked,
    handleEdgeDeleteFromDialog,
  } = useCanvasEventHandlers('screens', eventHandlerContext, v2ScreenRegistry as any, edgeDialogState);

  // Set template source on coordinator
  useEffect(() => {
    if (!coordinator) return;

    coordinator.setTemplateSource((templateId: string) => {
      return templatePanelRef.current?.getTemplateById(templateId) || null;
    });

    coordinator.setLocaleSettings(effectiveLanguages, effectiveDefaultLanguage);

    coordinator.setOnLaunchScreenChanged((newLaunchScreenId: string) => {
      console.log('[CanvasEditorV2] Launch screen changed to:', newLaunchScreenId);
      setLaunchScreenId(newLaunchScreenId);
    });

    console.log('[CanvasEditorV2] Template source, locale, and callback configured');
  }, [coordinator, effectiveLanguages, effectiveDefaultLanguage, setLaunchScreenId]);

  // Templates available to the click-to-add screen picker (same source the panel uses).
  const pickerTemplates = useMemo(() => {
    void registryVersion; // re-read after theme/template changes
    return templatePanelRef.current?.getTemplates() ?? [];
  }, [registryVersion, screenPickerOpen]);

  // Click-to-add: create a screen from a template at the viewport center (reuses
  // the same creation path as drag-and-drop via handleScreenDropped), then select
  // the newly created node so the user sees what was added.
  const handleAddScreenByTemplateId = useCallback(async (templateId: string): Promise<void> => {
    if (!templateId) return;
    const position = canvasHandle?.getViewportCenterPosition() ?? { x: 0, y: 0 };
    const before = new Set(v2ScreenRegistry.getAll().map((s) => s.entityId));
    await handleScreenDropped({ templateId, position, snapToGrid: true });
    const added = v2ScreenRegistry.getAll().find((s) => !before.has(s.entityId));
    if (added?.entityId) {
      canvasHandle?.selectNodes([added.entityId]);
    }
  }, [canvasHandle, handleScreenDropped]);

  // Wrapper to convert screenId → entityId
  const handleScreensMoved = useCallback((screens: Array<{ screenId: string; newPosition: { x: number; y: number } }>) => {
    const movedEntities = screens.map(s => ({ entityId: s.screenId, newPosition: s.newPosition }));
    return handleScreensMovedBase(movedEntities);
  }, [handleScreensMovedBase]);

  const loadCurrentFlowVariables = useCallback(async (): Promise<Record<string, FunnelVariable>> => {
    if (externalVariables) {
      return externalVariables;
    }
    if (variablesHost) {
      try {
        const freshVariables = await variablesHost.getFlowVariables();
        setFlowVariables(freshVariables);
        return freshVariables;
      } catch (err) {
        console.warn('[CanvasEditorV2] Failed to refresh coordinator flow variables:', err);
        return flowVariables;
      }
    }
    if (!onboardingId || !versionId) {
      return flowVariables;
    }

    try {
      const freshVariables = await flowVariablesService.getVariables(projectId, onboardingId, versionId);
      setFlowVariables(freshVariables);
      return freshVariables;
    } catch (err) {
      console.warn('[CanvasEditorV2] Failed to refresh flow variables for clipboard operation:', err);
      return flowVariables;
    }
  }, [externalVariables, variablesHost, flowVariables, onboardingId, projectId, versionId]);

  const canvasScreens = useMemo(() => {
    void registryVersion;
    return v2ScreenRegistry.getAll();
  }, [registryVersion]);

  const selectedCanvasScreens = useMemo(() => {
    const selected = new Set(selectedScreenIds);
    if (selected.size === 0) return [];

    const bySelectionId = new Map<string, FlowStepNode>();
    canvasScreens.forEach((screen) => {
      if (screen.entityId) bySelectionId.set(screen.entityId, screen);
      if (screen.id) bySelectionId.set(screen.id, screen);
    });

    return Array.from(selected)
      .map(id => bySelectionId.get(id) ?? v2ScreenRegistry.get(id))
      .filter((step): step is FlowStepNode => Boolean(step));
  }, [canvasScreens, selectedScreenIds]);

  const updateSelectedScreensBlockTags = useCallback(async (payload?: AssignBlockTagPayload) => {
    if (!coordinator || selectedCanvasScreens.length === 0) return;

    const normalizedBlockId = payload?.blockId.trim().replace(/^block:/, '');
    const normalizedStrategyId = payload?.strategyId?.trim().replace(/^strategy:/, '');
    const normalizedBlockDocId = payload?.blockDocId?.trim().replace(/^block-doc:/, '');
    if (payload !== undefined && !normalizedBlockId) return;

    setIsSyncing(true);
    setSyncOperation(payload ? 'Assigning block label' : 'Removing block labels');
    try {
      for (const step of selectedCanvasScreens) {
        const existingTags = Array.isArray(step.tags)
          ? step.tags.filter((tag): tag is string => typeof tag === 'string')
          : [];
        const nextTags = existingTags.filter(tag => (
          !tag.startsWith(STRATEGY_BLOCK_TAG_PREFIX)
          && !tag.startsWith(STRATEGY_BLOCK_DOC_TAG_PREFIX)
          && (!normalizedStrategyId || !tag.startsWith(STRATEGY_TAG_PREFIX))
        ));

        if (normalizedStrategyId) {
          nextTags.push(`${STRATEGY_TAG_PREFIX}${normalizedStrategyId}`);
        }
        if (normalizedBlockId) {
          nextTags.push(`${STRATEGY_BLOCK_TAG_PREFIX}${normalizedBlockId}`);
        }
        if (normalizedBlockDocId) {
          nextTags.push(`${STRATEGY_BLOCK_DOC_TAG_PREFIX}${normalizedBlockDocId}`);
        }

        await coordinator.updateEntityAndRefreshConnections(step.entityId, {
          ...step,
          tags: Array.from(new Set(nextTags)),
        });
      }
    } finally {
      setIsSyncing(false);
      setSyncOperation('');
    }
  }, [coordinator, selectedCanvasScreens, setIsSyncing, setSyncOperation]);

  const handleAssignBlockTag = useCallback(async (payload: AssignBlockTagPayload) => {
    await updateSelectedScreensBlockTags(payload);
  }, [updateSelectedScreensBlockTags]);

  const handleRemoveBlockTags = useCallback(async () => {
    await updateSelectedScreensBlockTags();
  }, [updateSelectedScreensBlockTags]);

  // Versioning seam for selective theme apply: today the apply targets the
  // active version the coordinator is bound to. A future "snapshot before
  // apply" creates a new version here and rebinds persistence to it.
  const resolveApplyTargetVersionId = useCallback(() => versionId, [versionId]);

  // Surface A — theme editor "Apply to canvas": current draft theme, scoped by screen types
  const handleApplyDraftThemeByTypes = useCallback(({ theme, screenTypes, templateIdByKind }: {
    theme: ThemeV2;
    screenTypes: string[];
    templateIdByKind?: Record<string, string>;
  }) => {
    // `screenTypes` carries template tile keys (kind, or kind::subtype for
    // variants). Match each canvas screen by its own (kind, subtype) tile key so
    // a quiz screen only follows the quiz tile, never its feedback sibling.
    const keys = new Set(screenTypes);
    const targets = canvasScreens.filter((screen) =>
      keys.has(templateTileKey(screen.screenType, screen.subtype)),
    );
    setApplyThemeDialog({
      theme,
      screens: targets,
      sourceLabel: 'Draft (unsaved)',
      scopeLabel: `Screen types: ${screenTypes
        .map((key) => key.replace('::', ' · ').replace(/([A-Z])/g, ' $1').trim())
        .join(', ')}`,
      templateIdByKind,
    });
  }, [canvasScreens]);

  // Surface B — bulk actions "Update UI from theme": saved settings, scoped by selected instances.
  // Re-reads themeSettingsV2 from Firestore first: the theme editor saves through its OWN
  // hook instance, so this hub's copy can be stale right after a theme Save → without the
  // refresh the engine would compare screens against the pre-edit theme ("Nothing to change").
  const handleApplyThemeToSelectedScreens = useCallback(async () => {
    if (selectedCanvasScreens.length === 0) return;
    const targets = selectedCanvasScreens;
    const fresh = strategyId ? strategyThemeSettings : await refreshOnboardingThemeSettings();
    const settings = fresh ?? themeSettingsV2;
    if (!settings) return;
    const theme = onboardingSettingsV2ToThemeV2(settings, {
      id: 'saved-theme-settings',
      name: 'Saved theme settings',
    });
    setApplyThemeDialog({
      theme,
      screens: targets,
      sourceLabel: 'Saved theme settings',
      scopeLabel: `${targets.length} selected screen${targets.length === 1 ? '' : 's'}`,
    });
  }, [selectedCanvasScreens, strategyId, strategyThemeSettings, refreshOnboardingThemeSettings, themeSettingsV2]);

  // After an onboarding theme switch: the toast's "Restyle screens…" opens the same Surface B dialog with
  // EVERY screen of the version in scope (nothing changes until Apply). Reads the switched theme fresh.
  const handleRestyleAfterThemeChange = useCallback(async () => {
    setThemeChangedName(null);
    const targets = canvasScreens;
    if (targets.length === 0) return;
    const settings = (await refreshOnboardingThemeSettings()) ?? themeSettingsV2;
    if (!settings) return;
    setApplyThemeDialog({
      theme: onboardingSettingsV2ToThemeV2(settings, {
        id: 'saved-theme-settings',
        name: 'Saved theme settings',
      }),
      screens: targets,
      sourceLabel: 'Saved theme settings',
      scopeLabel: `All screens (${targets.length})`,
    });
  }, [canvasScreens, refreshOnboardingThemeSettings, themeSettingsV2]);

  // Surface B (edit mode) — bulk actions "Edit theme & apply…": tweak the theme
  // inline, save it, and apply to the selected instances in one flow.
  const handleEditAndApplyThemeToSelectedScreens = useCallback(async () => {
    if (selectedCanvasScreens.length === 0) return;
    const targets = selectedCanvasScreens;
    const fresh = strategyId ? strategyThemeSettings : await refreshOnboardingThemeSettings();
    const settings = fresh ?? themeSettingsV2;
    if (!settings) return;
    const theme = onboardingSettingsV2ToThemeV2(settings, {
      id: 'saved-theme-settings',
      name: 'Saved theme settings',
    });
    setApplyThemeDialog({
      theme,
      screens: targets,
      sourceLabel: 'Edited theme',
      scopeLabel: `${targets.length} selected screen${targets.length === 1 ? '' : 's'}`,
      allowThemeEdit: true,
    });
  }, [selectedCanvasScreens, strategyId, strategyThemeSettings, refreshOnboardingThemeSettings, themeSettingsV2]);

  const persistAppliedThemeScreens = useCallback(async (payload: ApplyThemeGroupsConfirmPayload<FlowStepNode>) => {
    if (!coordinator) return;
    // A published (frozen) or non-head version is never written into — the same rule as the screen-editor
    // save (R7/P3). Throwing keeps the apply dialog open with its error alert.
    if (!strategyId && (frozen || viewingOnly)) {
      throw new Error('This version is read-only. Create an editable version, then restyle its screens.');
    }
    void resolveApplyTargetVersionId();
    setIsSyncing(true);
    setSyncOperation('Applying theme to screens');
    try {
      // When the user edited the theme inside the dialog AND chose to save it,
      // persist to themeSettingsV2. When saveTheme is false
      // the edit is a temporary theme — screens update but the saved theme is untouched.
      if (payload.themeWasEdited && payload.saveTheme) {
        const settings = themeV2ToOnboardingSettings(payload.theme, {
          sourceThemeId: themeSettingsV2?.sourceThemeId,
        });
        if (strategyId) {
          await updateStrategyTheme(settings);
        } else {
          await updateOnboardingThemeSettings(settings);
        }
      }
      for (const change of payload.changes) {
        const preservedAfter = preserveThemeApplyScreenContent(
          change.before as unknown as FlowStepNode,
          change.after as unknown as FlowStepNode,
        );
        const beforeRuntime = change.before as FlowStepNode & StrategyApplyScreenRuntimeFields;
        const targetBlockId = beforeRuntime.__strategyBlockId;
        const entityId = change.before.entityId ?? change.before.id;
        const strategyScreenId = beforeRuntime.__strategyScreenId || change.before.id || change.before.entityId;
        if (!entityId) continue;

        if (strategyId && targetBlockId && targetBlockId !== strategyBlockId) {
          if (!strategyScreenId) continue;
          await strategyService.replaceStepNodeScreen(
            strategyId,
            targetBlockId,
            strategyScreenId,
            stripStrategyApplyRuntimeFields(preservedAfter as unknown as Record<string, unknown>) as unknown as StepNode,
          );
        } else {
          await coordinator.updateEntityAndRefreshConnections(
            entityId,
            stripStrategyApplyRuntimeFields(preservedAfter as unknown as Record<string, unknown>) as unknown as FlowStepNode,
          );
        }
      }

      // Version-level animation seam: the Animations group also writes the
      // funnel-wide defaults (screensGraph.defaultTransition/defaultScreenAnimation)
      // that WebPlacementConfigLoader bakes at publish — screen-content apply
      // alone can never deliver a screen-to-screen transition. Onboarding scope
      // only; strategy retheme stays screens-only (blocks have no version doc).
      const animationSettings = payload.theme.animationSettings;
      if (!strategyId && payload.groups.includes('animations') && animationSettings) {
        const targetVersionId = resolveApplyTargetVersionId();
        if (targetVersionId) {
          await onboardingService.updateVersionAnimationDefaults(
            projectId,
            onboardingId,
            targetVersionId,
            {
              defaultTransition: animationSettings.transition as unknown as Record<string, unknown> | null | undefined,
              defaultScreenAnimation: animationSettings.screenAnimation as unknown as Record<string, unknown> | null | undefined,
              // Always map-or-null (never undefined): switching to a scheme
              // without per-kind overrides must ERASE a stale map, not skip it
              perKindTransitions: buildPerKindTransitions(animationSettings) as unknown as Record<string, unknown> | null,
            }
          );
          // Re-read the defaults so the flowDocument memo (and with it the
          // screen editor preview's theme-inherited entrance) updates without
          // reopening the canvas (optional coordinator capability)
          await reloadFlowAnimationDefaults(coordinator);
          setAnimationDefaultsVersion((n) => n + 1);
        }
      }
    } finally {
      setIsSyncing(false);
      setSyncOperation('');
    }
    const skipped = payload.skippedNoop.length + payload.optedOut.length;
    setApplyThemeSummary(
      `Applied ${payload.changes.length} · Skipped ${skipped} · Unsupported ${payload.unsupported.length}`
    );
  }, [
    coordinator,
    resolveApplyTargetVersionId,
    setIsSyncing,
    setSyncOperation,
    strategyId,
    strategyBlockId,
    strategyService,
    themeSettingsV2,
    updateOnboardingThemeSettings,
    updateStrategyTheme,
    onboardingService,
    projectId,
    onboardingId,
    frozen,
    viewingOnly,
  ]);

  // Copy selected V2 screens to the system clipboard.
  // Reads FlowStepNodes from v2ScreenRegistry by the current selection and
  // writes them as a V2-marker payload. No-op when nothing is selected.
  const handleCopySelectedScreens = useCallback(async () => {
    if (selectedScreenIds.length === 0) return;

    if (selectedCanvasScreens.length === 0) return;

    const currentVariables = await loadCurrentFlowVariables();
    const transplantPayload = strategyId && strategyBlockId
      ? prepareStrategyScreenTransplant({
          sourceStrategyId: strategyId,
          sourceBlock: {
            id: strategyBlockId,
            entityId: strategyBlockEntityId || strategyBlockId,
            screens: v2ScreenRegistry.getAll() as unknown as StrategyFlowScreen[],
          },
          selectedScreenIds: selectedCanvasScreens.map(step => step.entityId).filter((id): id is string => Boolean(id)),
          membership: { mode: 'no-block' },
        })
      : null;
    const copySteps = (transplantPayload?.screens as unknown as FlowStepNode[] | undefined) ?? selectedCanvasScreens;
    const variablePayload = v2ClipboardVariablePortabilityService.buildClipboardPayload({
      steps: copySteps,
      selectedStepIds: copySteps.map(step => step.entityId),
      variables: currentVariables,
      source: { projectId, onboardingId, versionId },
    });

    await v2CanvasClipboardService.copy(copySteps, {
      variables: variablePayload.variables,
      variableDependencyIds: variablePayload.variableDependencyIds,
      warnings: [...(transplantPayload?.warnings ?? []), ...(variablePayload.warnings ?? [])],
      source: {
        projectId,
        onboardingId,
        versionId,
        strategyTransplant: transplantPayload?.source,
      },
    });

    const copyWarnings = [...(transplantPayload?.warnings ?? []), ...(variablePayload.warnings ?? [])];
    if (copyWarnings.length) {
      setPasteWarning(copyWarnings.join('\n'));
    }
    console.log(`[CanvasEditorV2] Copied ${selectedCanvasScreens.length} screen(s) to clipboard`);
  }, [loadCurrentFlowVariables, onboardingId, projectId, selectedCanvasScreens, selectedScreenIds.length, strategyBlockEntityId, strategyBlockId, strategyId, versionId]);

  // Paste V2 screens from the system clipboard via the V2 paste pipeline.
  // Silent no-op when the clipboard contains non-V2 data (empty, plain text,
  // or a V1 Segmently payload).
  const handlePasteScreens = useCallback(async () => {
    if (!coordinator) return;

    const payload = await v2CanvasClipboardService.read();
    if (!payload) return; // Non-V2 clipboard → silent no-op

    // Gather existing step names from the coordinator's registry for
    // batch-aware dedup ("Hero" → "Hero (Copy)", etc.)
    const existingNames = coordinator
      .getAllEntities()
      .map((s: FlowStepNode) => s.name || '');

    const result = v2StepPasteProcessor.process({
      clipboardSteps: payload.steps,
      originalStepIds: payload.originalStepIds,
      existingStepNames: existingNames,
      positionOffset: { x: 150, y: 150 },
    });

    if (result.steps.length === 0) return;

    const warnings: string[] = [...(payload.warnings || [])];
    const canPersistVariables = Boolean(variablesHost)
      || Boolean(onboardingId && versionId && !externalVariables);
    const targetStrategyId = payload.source?.strategyTransplant
      ? await resolveTargetStrategyIdForPaste()
      : targetStrategyIdForPaste;
    const transplantPolicy = applyStrategyScreenTransplantPastePolicy({
      screens: result.steps as unknown as StrategyFlowScreen[],
      source: payload.source?.strategyTransplant,
      targetStrategyId,
    });
    let stepsToPaste = transplantPolicy.screens as unknown as FlowStepNode[];
    let variablesToCreate: FunnelVariable[] = [];

    if (payload.variables && Object.keys(payload.variables).length > 0) {
      if (canPersistVariables) {
        const targetVariables = await loadCurrentFlowVariables();
        const variablePlan = v2ClipboardVariablePortabilityService.planPasteVariables({
          clipboardVariables: payload.variables,
          originalStepIds: payload.originalStepIds,
          stepIdMapping: result.idMapping,
          targetVariables,
        });

        variablesToCreate = variablePlan.variablesToCreate;
        stepsToPaste = v2ClipboardVariablePortabilityService.rewriteStepVariableReferences({
          steps: stepsToPaste,
          variableIdMapping: variablePlan.variableIdMapping,
          variableRefMapping: variablePlan.variableRefMapping,
        });
        warnings.push(...variablePlan.warnings);
      } else {
        warnings.push('Variable dependencies were not imported because this canvas context cannot persist flow variables.');
      }
    }

    // Execute via PasteV2EntitiesCommand so the paste is a single undoable unit
    const command = new PasteV2EntitiesCommand(stepsToPaste, coordinator, {
      variablesToCreate,
      variablePersistence: variablesToCreate.length > 0 && canPersistVariables
        ? {
            saveVariables: async (variables) => {
              if (variablesHost) {
                for (const variable of variables) {
                  await variablesHost.saveFlowVariable(variable);
                }
                return;
              }
              await Promise.all(variables.map(variable =>
                flowVariablesService.saveVariable(projectId, onboardingId, versionId, variable)
              ));
            },
            deleteVariables: async (variableIds) => {
              if (variablesHost) {
                for (const variableId of variableIds) {
                  await variablesHost.deleteFlowVariable(variableId);
                }
                return;
              }
              await Promise.all(variableIds.map(variableId =>
                flowVariablesService.deleteVariable(projectId, onboardingId, versionId, variableId)
              ));
            },
          }
        : undefined,
    });
    const cmdResult = await commandManager.execute(command);

    if (cmdResult.success) {
      // Select the newly pasted steps on the canvas. Force derived canvas
      // recalculation by bumping registryVersion because EntityRegistry.register()
      // does not notify listeners for silent writes.
      settersRef.current.setSelectedScreenIds(stepsToPaste.map(s => s.entityId));
      setRegistryVersion(v => v + 1);
      if (variablesToCreate.length > 0 && canPersistVariables) {
        const freshVariables = variablesHost
          ? await variablesHost.getFlowVariables()
          : await flowVariablesService.getVariables(projectId, onboardingId, versionId);
        setFlowVariables(freshVariables);
      }
      if (warnings.length > 0) {
        setPasteWarning(warnings.join('\n'));
      }
      console.log(`[CanvasEditorV2] Pasted ${stepsToPaste.length} screen(s)`);
    }
  }, [
    coordinator,
    commandManager,
    externalVariables,
    loadCurrentFlowVariables,
    onboardingId,
    projectId,
    resolveTargetStrategyIdForPaste,
    settersRef,
    targetStrategyIdForPaste,
    variablesHost,
    versionId,
  ]);

  // Keyboard shortcuts (undo/redo, search — copy/paste disabled)
  useEffect(() => {
    const handleKeyboard = async (e: KeyboardEvent) => {
      if (!strategyId && associatedStrategyOverlayOpen) return;
      if (isKeyboardCapturedByOverlay(e)) return;
      // Spec D D1b (AC-D1b-1): analytics mode is read-only — no edit shortcuts
      // (undo/paste/add-screen would target the unmounted editor canvas).
      if (insightsOverlayEnabled) return;

      // Cmd/Ctrl+Z → undo, Cmd/Ctrl+Shift+Z → redo
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          if (commandManager.canRedo()) await commandManager.redo();
        } else {
          if (commandManager.canUndo()) await commandManager.undo();
        }
      }

      // Cmd/Ctrl+F → open screen search (hidden in strategy mode)
      if (!hideLocaleTools && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        setSearchOpen(true);
      }

      // Cmd/Ctrl+X → open the click-to-add screen picker (onboarding canvas only)
      if (isOnboardingHost && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'x') {
        e.preventDefault();
        setScreenPickerOpen(true);
      }

      // Copy/paste handlers present but log not-implemented
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') {
        if (selectedScreenIds.length > 0) {
          e.preventDefault();
          await handleCopySelectedScreens();
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        await handlePasteScreens();
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [
    associatedStrategyOverlayOpen,
    commandManager,
    handleCopySelectedScreens,
    handlePasteScreens,
    insightsOverlayEnabled,
    isOnboardingHost,
    selectedScreenIds,
    strategyId,
  ]);

  /**
   * Handle node double-click — opens V2 screen editor directly.
   *
   * Sprint 3: No adapter conversion needed — V2 dialog works with StepNode natively.
   */
  const handleNodeDoubleClick = useCallback((entityId: string) => {
    const step = v2ScreenRegistry.get(entityId);
    if (!step) {
      console.warn('[CanvasEditorV2] Screen not found:', entityId);
      return;
    }

    console.log('[CanvasEditorV2] Opening V2 editor for screen:', step.name || entityId);

    editingEntityIdRef.current = entityId;
    setSelectedStepForEdit(step);
    setScreenEditorOpen(true);
    onScreenEditorOpen?.(entityId);
  }, [onScreenEditorOpen]);

  // Handle node action
  const handleNodeAction = useCallback(async (entityId: string, actionKey: string, _entityType?: string) => {
    if (actionKey === 'edit') {
      handleNodeDoubleClick(entityId);
      return;
    }

    if (!eventHandlerContext) return;

    const flowCanvasEventHandler = await import('@modules/reactFlow/services/shared/FlowCanvasEventHandler').then(m => m.flowCanvasEventHandler);
    await flowCanvasEventHandler.handleEvent({
      type: 'node.action',
      timestamp: Date.now(),
      entityId,
      actionKey,
      entityType: 'onboarding-screen',
    } as any, eventHandlerContext);
  }, [eventHandlerContext, handleNodeDoubleClick]);

  /**
   * Handle step saved from V2 editor dialog.
   *
   * Sprint 3: Receives FlowStepNode directly — no V1↔V2 conversion needed.
   * Preserves position from registry and saves via V2 coordinator.
   */
  const handleEditorStepSaved = useCallback(async (savedStep: FlowStepNode) => {
    if (!coordinator) return;

    const entityId = editingEntityIdRef.current || savedStep.entityId || savedStep.id;
    console.log('[CanvasEditorV2] V2 editor saved step:', entityId);

    // Preserve position from registry (editor doesn't modify position)
    const currentStep = v2ScreenRegistry.get(entityId);
    const position = currentStep?.position || { x: 0, y: 0 };

    // Build FlowStepNode with preserved entityId and position
    const flowStep: FlowStepNode = {
      ...savedStep,
      id: entityId,
      entityId,
      position,
    };

    // R7/P3 (no-dead-end): the selected version is frozen — never write into it.
    // Retarget the SAVE to the owner, which mints a new version carrying this
    // payload and switches the editor there (key-prop remount shows it saved).
    // U13 shares the exact same path: a non-head version is never written into.
    if ((frozen || viewingOnly) && onSaveToNewVersion) {
      await onSaveToNewVersion(flowStep);
      return;
    }

    // Save via V2 coordinator
    await coordinator.updateEntityAndRefreshConnections(entityId, flowStep);

    // Force derived canvas recalculation
    setRegistryVersion(v => v + 1);

    // Re-fetch flow variables — editor may have created/updated variables via VariableEditorContext
    // Skip in strategy mode (uses externalVariables)
    if (variablesHost) {
      variablesHost.getFlowVariables()
        .then(setFlowVariables)
        .catch((err) => console.warn('[CanvasEditorV2] Failed to refresh coordinator flow variables:', err));
    } else if (onboardingId && versionId && !externalVariables) {
      flowVariablesService.getVariables(projectId, onboardingId, versionId)
        .then(setFlowVariables)
        .catch((err) => console.warn('[CanvasEditorV2] Failed to refresh flow variables:', err));
    }

    console.log('[CanvasEditorV2] V2 screen updated from editor:', entityId);
  }, [coordinator, variablesHost, projectId, onboardingId, versionId, frozen, onSaveToNewVersion]);

  // Load flow variables for V2 condition editor (async, independent of canvas loading)
  // In strategy mode, use externalVariables directly instead of loading from flowVariablesService.
  // Variables-hosting coordinators (articles) load from the coordinator instead.
  useEffect(() => {
    if (!open) return;
    if (externalVariables) {
      setFlowVariables(externalVariables);
      return;
    }
    if (variablesHost) {
      let isActive = true;
      variablesHost.getFlowVariables()
        .then((vars) => { if (isActive) setFlowVariables(vars); })
        .catch((err) => console.warn('[CanvasEditorV2] Failed to load coordinator flow variables:', err));
      return () => { isActive = false; };
    }
    if (!onboardingId || !versionId) return;
    let isActive = true;
    flowVariablesService.getVariables(projectId, onboardingId, versionId)
      .then((vars) => { if (isActive) setFlowVariables(vars); })
      .catch((err) => console.warn('[CanvasEditorV2] Failed to load flow variables:', err));
    return () => { isActive = false; };
  }, [open, projectId, onboardingId, versionId, externalVariables, variablesHost]);

  // Assemble FlowDocument for V2 condition editor
  const flowDocument = useMemo((): FlowDocument | null => {
    if (!coordinator) return null;
    const screens: Record<string, StepNode> = {};
    for (const step of coordinator.getAllEntities()) {
      if (step.entityId) {
        screens[step.entityId] = step as unknown as StepNode;
      }
    }
    // Version-level animation defaults ride along so the screen editor
    // preview can play theme-inherited entrances ("Inherited from theme"
    // hint + Replay). animationDefaultsVersion bumps after Apply→Animations
    // rewrites them mid-session. Optional coordinator capability: the article
    // and strategy-block coordinators have none and read as nulls.
    const animationDefaults = readFlowAnimationDefaults(coordinator);
    return {
      schemaVersion: '2.0.3',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      defaultLanguage: effectiveDefaultLanguage as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      languages: effectiveLanguages as any,
      launchScreenId: launchScreenId || '',
      screens,
      // Document metadata exposed by the coordinator (version.screensGraph for
      // onboardings, flowDocument.metadata for articles). Structural read —
      // every coordinator variant carries the public field; kind: 'article'
      // gates article-only editor features (container presets, profile chips).
      metadata: (coordinator as { screensGraphMetadata?: Record<string, unknown> | null }).screensGraphMetadata ?? {},
      variables: flowVariables,
      ...(animationDefaults.defaultScreenAnimation
        ? { defaultScreenAnimation: animationDefaults.defaultScreenAnimation }
        : {}),
      ...(animationDefaults.defaultTransition
        ? { defaultTransition: animationDefaults.defaultTransition }
        : {}),
    } as unknown as FlowDocument;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinator, registryVersion, launchScreenId, flowVariables, effectiveDefaultLanguage, effectiveLanguages, animationDefaultsVersion]);

  // Create the coordinator: the host's coordinatorFactory (article draft, strategy block) or,
  // without one, V2ScreenFlowCoordinator for the onboarding version.
  useEffect(() => {
    if (!open) return;

    setIsLoading(true);

    const newCoordinator = coordinatorFactory
      ? coordinatorFactory()
      : new V2ScreenFlowCoordinator(projectId, onboardingId, versionId);

    newCoordinator.setLoadingCallbacks({
      onSyncStart: (operation: string) => {
        console.log(`[CanvasEditorV2] Firebase sync START: ${operation}`);
        setIsSyncing(true);
        setSyncOperation(operation);
      },
      onSyncEnd: () => {
        console.log('[CanvasEditorV2] Firebase sync END');
        setIsSyncing(false);
        setSyncOperation('');
      },
      onSyncError: (error: Error, operation: string) => {
        console.error(`[CanvasEditorV2] Firebase sync ERROR during ${operation}:`, error);
        setIsSyncing(false);
        setSyncOperation('');
      }
    });

    setCoordinator(newCoordinator);

    return () => {
      newCoordinator.destroy();
      autoFocusTriggeredRef.current = false;
    };
  }, [open, projectId, onboardingId, versionId, setIsLoading, setIsSyncing, setSyncOperation, coordinatorFactory]);

  // Load data and register canvas handle
  useEffect(() => {
    if (!coordinator || !canvasHandle || !open) return;

    let isActive = true;

    const initCoordinator = async () => {
      try {
        setIsLoading(true);
        await coordinator.loadScreensFromFirebase();

        if (!isActive) return;

        coordinator.setCanvasHandle(canvasHandle);

        const launchId = await coordinator.getLaunchEntityId();
        if (launchId) {
          setLaunchScreenId(launchId);
        }
      } catch (error) {
        console.error('[CanvasEditorV2] Initialization error:', error);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    initCoordinator();

    return () => {
      isActive = false;
    };
  }, [coordinator, canvasHandle, open, setIsLoading, setLaunchScreenId]);

  // Auto-focus on launch screen after loading
  useEffect(() => {
    if (isLoading || !canvasHandle || autoFocusTriggeredRef.current) return;

    autoFocusTriggeredRef.current = true;

    const rafId = requestAnimationFrame(() => {
      const focusScreenParam = typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('focusScreen')
        : null;
      if (focusScreenParam) {
        console.log('[CanvasEditorV2] Focusing on screen from focusScreen param:', focusScreenParam);
        canvasHandle.focusOnScreen(focusScreenParam, 0.5);
      } else if (launchScreenId) {
        console.log('[CanvasEditorV2] Auto-focusing on launch screen:', launchScreenId);
        canvasHandle.focusOnScreen(launchScreenId, 0.35);
      } else {
        console.log('[CanvasEditorV2] No launch screen, calling fitView');
        canvasHandle.fitView();
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [isLoading, canvasHandle, launchScreenId]);

  // PE-4 (ux-rework-handoff.md A5): `?insights=1` deep link arrives with the
  // analytics overlay already on — the exact toolbar-toggle enable sequence.
  // One-shot per mount: waits for the loaded graph (the snapshot must capture
  // real nodes) and for the capability read; a denied capability consumes the
  // param silently, same as the toolbar toggle being absent.
  const insightsDeepLinkTriggeredRef = useRef(false);
  useEffect(() => {
    if (insightsDeepLinkTriggeredRef.current) return;
    if (isLoading || !canvasHandle || insightsAccess.loading) return;
    if (!projectId || !onboardingId || !versionId) return;
    const insightsParam = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('insights')
      : null;
    insightsDeepLinkTriggeredRef.current = true;
    if (insightsParam !== '1' || !canUseInsightsOverlay) return;
    setAnalyticsSnapshot(canvasHandle.getGraphSnapshot() ?? { nodes: [], edges: [] });
    setInsightsOverlayEnabled(true);
    setSelectedInsightsEdge(null);
    setSelectedInsightsNode(null);
  }, [
    isLoading,
    canvasHandle,
    insightsAccess.loading,
    canUseInsightsOverlay,
    projectId,
    onboardingId,
    versionId,
  ]);

  useEffect(() => {
    if (lastAutoOpenScreenIdRef.current !== autoOpenScreenId) {
      autoOpenTriggeredRef.current = Boolean(
        screenEditorOpen
        && autoOpenScreenId
        && editingEntityIdRef.current === autoOpenScreenId
      );
      lastAutoOpenScreenIdRef.current = autoOpenScreenId;
    }
  }, [autoOpenScreenId, screenEditorOpen]);

  // Auto-open screen editor for specified screenId
  useEffect(() => {
    if (
      isLoading ||
      !autoOpenScreenId ||
      !coordinator ||
      autoOpenTriggeredRef.current ||
      screenEditorOpen
    ) {
      return;
    }

    autoOpenTriggeredRef.current = true;

    const step = v2ScreenRegistry.get(autoOpenScreenId);
    if (step) {
      console.log('[CanvasEditorV2] Auto-opening V2 screen editor for:', step.name || autoOpenScreenId);

      canvasHandle?.focusOnScreen(autoOpenScreenId, 0.35);
      editingEntityIdRef.current = autoOpenScreenId;
      setSelectedStepForEdit(step);
      setScreenEditorOpen(true);
    } else {
      console.warn('[CanvasEditorV2] Auto-open screen not found:', autoOpenScreenId);
    }
  }, [isLoading, autoOpenScreenId, coordinator, screenEditorOpen, canvasHandle]);

  // Node view factory
  const nodeViewFactory = useCallback((screen: any) => {
    const type = screen.screenType?.toUpperCase() || 'UNKNOWN';
    const name = typeof screen.name === 'string' ? screen.name : (screen.id || 'Unnamed');
    return `${type}\n${name}`;
  }, []);

  // Build variable usage map: variableId → stepIds that bind it
  // Now reads from FunnelVariable.boundScreenIds (variable is the single source of truth)
  const screensBindingMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const variable of Object.values(flowVariables)) {
      const boundScreenIds = (variable as unknown as Record<string, string[]>).boundScreenIds;
      if (!boundScreenIds?.length) continue;
      map[variable.id] = [...boundScreenIds];
    }
    return map;
  }, [flowVariables]);

  // Canvas rework T5: the inverse view — screenId → bound variables, distilled
  // for the analytics banner chips (excluded ones render muted «private»).
  const screenBoundVariables = useMemo(() => {
    const map: Record<string, Array<{ id: string; name: string; excludeFromAnalytics?: boolean }>> = {};
    for (const variable of Object.values(flowVariables)) {
      const boundScreenIds = (variable as unknown as { boundScreenIds?: string[] }).boundScreenIds;
      if (!boundScreenIds?.length) continue;
      for (const screenId of boundScreenIds) {
        (map[screenId] ??= []).push({
          id: variable.id,
          name: variable.name,
          excludeFromAnalytics: (variable as { excludeFromAnalytics?: boolean }).excludeFromAnalytics,
        });
      }
    }
    return map;
  }, [flowVariables]);

  // Handle edit conditions from TransitionEditor (V2 native — reads RouteGuard[] directly)
  const handleEditConditions = useCallback(async (
    edgeId: string,
    sourceScreenId: string,
    targetScreenId: string,
    _currentRules: unknown  // V1 Condition[] from TransitionEditor — ignored, we read V2 directly
  ) => {
    const step = v2ScreenRegistry.get(sourceScreenId);
    const freshConditions: RouteGuard[] = step
      ? v2TransitionManager.getTransitionRouteGuards(step as StepNode, edgeId)
      : [];

    const sourceStepName = (step as any)?.name || sourceScreenId.slice(0, 12);
    const targetStep = v2ScreenRegistry.get(targetScreenId);
    const targetStepName = (targetStep as any)?.name || targetScreenId.slice(0, 12);

    // Await fresh variables before opening condition editor (may have changed since canvas load)
    try {
      const freshVars = variablesHost
        ? await variablesHost.getFlowVariables()
        : await flowVariablesService.getVariables(projectId, onboardingId, versionId);
      setFlowVariables(freshVars);
    } catch (err) {
      console.warn('[CanvasEditorV2] Failed to refresh flow variables:', err);
    }

    setConditionEditorData({
      edgeId,
      sourceScreenId,
      targetScreenId,
      sourceStepName,
      targetStepName,
      currentConditions: freshConditions,
    });
    setConditionEditorOpen(true);
    setEditorOpen(false);
  }, [projectId, onboardingId, versionId, variablesHost]);

  // Handle save conditions (V2 native — writes RouteGuard[] with logic connectors)
  const handleConditionsSave = useCallback(async (conditions: RouteGuard[]) => {
    if (!coordinator || !conditionEditorData) return;

    const { edgeId, sourceScreenId } = conditionEditorData;

    const step = v2ScreenRegistry.get(sourceScreenId);
    if (!step) {
      console.error('[CanvasEditorV2] Screen not found:', sourceScreenId);
      return;
    }

    console.log('[CanvasEditorV2] Saving V2 conditions:', { edgeId, count: conditions.length });

    const updatedStepNode = v2TransitionManager.updateTransitionRouteGuards(
      step as StepNode,
      edgeId,
      conditions,
    );

    const updatedFlowStep: FlowStepNode = {
      ...updatedStepNode,
      entityId: step.entityId,
    } as FlowStepNode;

    await coordinator.updateEntityAndRefreshConnections(sourceScreenId, updatedFlowStep);

    setConditionEditorOpen(false);
    setConditionEditorData(null);
  }, [coordinator, conditionEditorData]);

  // Per-edge visual transition override (Route.transition) — mirrors
  // handleConditionsSave: fresh registry read → manager update → full
  // replaceScreen + syncEdges (badge refresh rides the edge data rebuild)
  const handleEdgeTransitionSave = useCallback(async (
    edgeId: string,
    sourceScreenId: string,
    transition: ScreenTransition | null,
  ) => {
    if (!coordinator) return;

    const step = v2ScreenRegistry.get(sourceScreenId);
    if (!step) {
      console.error('[CanvasEditorV2] Screen not found:', sourceScreenId);
      return;
    }

    console.log('[CanvasEditorV2] Saving edge transition override:', { edgeId, type: transition?.type ?? 'inherit' });

    const updatedStepNode = v2TransitionManager.updateTransitionScreenTransition(
      step as StepNode,
      edgeId,
      transition,
    );

    const updatedFlowStep: FlowStepNode = {
      ...updatedStepNode,
      entityId: step.entityId,
    } as FlowStepNode;

    await coordinator.updateEntityAndRefreshConnections(sourceScreenId, updatedFlowStep);
  }, [coordinator]);

  const insightsTooltip = funnelOverlay.error
    ? `Insights unavailable: ${funnelOverlay.error}`
    : insightsOverlayEnabled
      ? 'Hide funnel insights'
      : 'Show funnel insights';

  const leftTopPanelActions = isOnboardingHost ? (
    <OnboardingBlockTagControls
      projectId={projectId}
      selectedScreenIds={selectedScreenIds}
      screens={canvasScreens}
      disabled={isSyncing}
      isAdmin={isAdmin}
      associatedStrategyId={targetStrategyIdForPaste}
      associatedStrategyName={associatedStrategy?.name}
      onAssignBlockTag={handleAssignBlockTag}
      onRemoveBlockTags={handleRemoveBlockTags}
      onRequestAssociateStrategy={openStrategyAssociationDialog}
      onOpenAssociatedStrategy={openAssociatedStrategy}
      onUpdateUiFromTheme={handleApplyThemeToSelectedScreens}
      onEditAndApplyTheme={handleEditAndApplyThemeToSelectedScreens}
      updateUiFromThemeDisabled={!themeSettingsV2}
      updateUiFromThemeDisabledReason={!themeSettingsV2 ? 'No saved theme settings for this onboarding' : undefined}
    />
  ) : null;

  const canvasGuideAction = isOnboardingHost ? (
    <WithGuideHelp
      guideKey={CANVAS_OVERVIEW_ANCHOR.guideKey}
      guideService={guideService}
      data-testid={CANVAS_OVERVIEW_ANCHOR.testId}
      position="inline-end"
    >
      <Box component="span" sx={{ width: 0, height: 0, overflow: 'hidden' }} />
    </WithGuideHelp>
  ) : null;

  const topPanelActions = strategyId && strategyBlockId ? (
    <StrategyCanvasActions
      mode="screens"
      onExport={handleExportBlock}
      onImport={() => setBlockImportOpen(true)}
      onPullFromOnboarding={() => handleOpenBlockSyncDialog('pull-block')}
      onPushToOnboarding={() => handleOpenBlockSyncDialog('push-block')}
      pullDisabled={syncLoading}
      pushDisabled={syncLoading}
      onEditTheme={handleOpenReplaceTheme}
      onReplaceTheme={handleOpenReplaceTheme}
      onUpdateUiFromTheme={handleApplyCurrentStrategyThemeToAllScreens}
      themeDisabled={themeReplaceLoading}
      replaceThemeDisabled={themeReplaceLoading}
      updateUiFromThemeDisabled={!strategyThemeSettings}
    />
  ) : analyticsModeActive ? (
    // Report mode (P-6): the edit actions are NOT rendered — the report bar
    // owns the header slot. Edit mode below stays byte-identical.
    <ReportModeBar
      onExit={exitReportMode}
      versionId={versionId}
      versionName={versionName}
      versionStarts={traffickedVersions.versions.find(candidate => candidate.versionId === versionId)?.starts}
      windowFrom={insightsWindow?.from}
      windowTo={insightsWindow?.to}
      filters={insightsFilters}
      filtersContent={(
        // I-B2 (P-7): the clause panel REPLACES the strip in report mode.
        <ReportFilterPanel
          options={funnelOverlay.filterOptions}
          value={insightsFilters}
          onApply={setInsightsFilters}
          userTraitOptions={funnelOverlay.userTraitOptions}
          projectId={projectId}
        />
      )}
      refreshing={funnelOverlay.loading}
      onRefresh={funnelOverlay.refresh}
      servedFromCache={funnelOverlay.servedFromCache}
      cachedAt={funnelOverlay.cachedAt}
      unit={funnelUnit}
      onUnitChange={handleFunnelUnitChange}
    />
  ) : (
    <Stack direction="row" spacing={1} alignItems="center">
      {customActions}
      {canUseInsightsOverlay && (
        <Tooltip title={insightsTooltip}>
          <span>
            <IconButton
              aria-label="toggle funnel insights"
              data-testid="funnel-insights-toggle"
              size="small"
              color={insightsOverlayEnabled ? 'primary' : 'default'}
              disabled={!projectId || !onboardingId || !versionId}
              onClick={() => {
                if (insightsOverlayEnabled) {
                  exitReportMode();
                } else {
                  enterReportMode();
                }
              }}
            >
              {funnelOverlay.loading && insightsOverlayEnabled ? (
                <CircularProgress size={18} />
              ) : (
                <QueryStatsIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {insightsOverlayEnabled && canUseInsightsOverlay && (
        <>
          <Tooltip title="Refresh funnel insights">
            <span>
              <IconButton
                aria-label="refresh funnel insights"
                data-testid="funnel-insights-refresh"
                size="small"
                disabled={funnelOverlay.loading}
                onClick={funnelOverlay.refresh}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {/* FU part F (D-F1 pattern): honest snapshot marker — refresh bypasses the cache. */}
          {funnelOverlay.servedFromCache && (
            <Tooltip title={funnelOverlay.cachedAt
              ? `Saved snapshot from ${new Date(funnelOverlay.cachedAt).toLocaleString()} — refresh to reload`
              : 'Saved snapshot — refresh to reload'}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid={ANALYTICS_CANVAS_TEST_IDS.overlayCachedMarker}
              >
                cached
              </Typography>
            </Tooltip>
          )}
          {/* P4: the legacy filter strip is GONE — filters live exclusively in
              the report bar's clause panel; this branch only shows while the
              overlay snapshot is still loading. */}
        </>
      )}
    </Stack>
  );

  if (!open) return null;

  // Canvas content
  const canvasContent = (
    // F2: the unit provider covers the WHOLE canvas surface — report bar,
    // honesty banner, react-flow node/edge renderers and the drawers — so no
    // unit prop drills through the reactFlow layer (Context-for-DI, §6).
    <FunnelUnitProvider unit={funnelUnit}>
    <FontLabelsProvider projectId={projectId}>
    <FontConfigFromProjectProvider projectId={projectId}>
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* Left: Top Panel + Canvas */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!hideTopPanel && (
          <TopControlPanel
            strategyName=""
            blockName={onboardingName || 'Onboarding Flow Editor'}
            currentMode="screens"
            commandManager={commandManager}
            undoCount={commandManager.getUndoHistory().length}
            redoCount={commandManager.getRedoHistory().length}
            onClose={onClose}
            isSyncing={isSyncing}
            leftCustomActions={analyticsModeActive ? null : leftTopPanelActions}
            customActions={topPanelActions}
            trailingActions={canvasGuideAction}
            hideHistoryControls={analyticsModeActive}
          />
        )}

        {/* Published (frozen) version — read-only guard (Spec A / D51, A4-3). */}
        {!strategyId && frozen && (
          <Alert
            severity="info"
            data-testid="onboarding-frozen-banner"
            action={
              onCreateEditableVersion ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={onCreateEditableVersion}
                  data-testid="onboarding-create-editable-version"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Create editable version
                </Button>
              ) : undefined
            }
            sx={{ borderRadius: 0 }}
          >
            This version is published and read-only. Create a new version to edit —
            the published version stays frozen.
          </Alert>
        )}

        {/* U13 (Spec D2, AC-D2-8): viewing a non-head version — read-only. */}
        {!strategyId && !frozen && viewingOnly && (
          <Alert
            severity="info"
            data-testid="onboarding-viewing-only-banner"
            action={
              <>
                {onBackToLatest && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={onBackToLatest}
                    data-testid="onboarding-back-to-latest"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Back to latest
                  </Button>
                )}
                {onCreateEditableVersion && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={onCreateEditableVersion}
                    data-testid="onboarding-fork-from-version"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Fork from this version
                  </Button>
                )}
              </>
            }
            sx={{ borderRadius: 0 }}
          >
            Viewing {viewingOnlyVersionName ?? 'an older version'} — editing is disabled.
            Saves retarget into a fresh version; this one stays intact.
          </Alert>
        )}

        {/* Canvas area */}
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {/* Loading overlay */}
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
              }}
            >
              <Box
                sx={{
                  width: 48, height: 48,
                  border: '4px solid rgba(255, 255, 255, 0.2)',
                  borderTop: '4px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <Typography variant="h6" sx={{ color: 'white', mt: 2 }}>
                Loading screens...
              </Typography>
            </Box>
          )}

          {/* Canvas toolbar — Search & Locale (hidden in strategy mode and read-only analytics mode) */}
          {!isLoading && !hideLocaleTools && !analyticsModeActive && (
            <Box
              sx={{
                position: 'absolute',
                top: 12, right: 12,
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <CanvasScreenSearch
                open={searchOpen}
                onOpenChange={setSearchOpen}
                onScreenSelected={(screenId) => {
                  canvasHandle?.focusOnScreen(screenId, 0.35);
                }}
              />
              {effectiveLanguages.length > 1 && (
                <CanvasLocaleSelector
                  locales={effectiveLanguages}
                  currentLocale={canvasLocale}
                  defaultLocale={effectiveDefaultLanguage}
                  onLocaleChange={setCanvasLocale}
                />
              )}
              {/* Last in the column — biggest target, easiest to reach. */}
              {isOnboardingHost && (
                <Tooltip title="Add screen (⌘/Ctrl+X)" placement="left">
                  <IconButton
                    onClick={() => setScreenPickerOpen(true)}
                    size="small"
                    data-testid="canvas-add-screen-btn"
                    sx={{
                      bgcolor: 'background.paper',
                      boxShadow: 2,
                      '&:hover': { bgcolor: 'action.hover' },
                      width: 36,
                      height: 36,
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          {/* Canvas — analytics mode swaps the editor for the read-only canvas (Spec D D1b) */}
          <CanvasLocaleContext.Provider value={{ locale: canvasLocale }}>
          <CanvasVariablesContext.Provider value={canvasPreviewVariables}>
          <CanvasBadgesContext.Provider value={canvasBadges}>
            {analyticsModeActive ? (
              <Box
                data-testid={ANALYTICS_CANVAS_TEST_IDS.container}
                sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
              >
                <AnalyticsHonestyBanner
                  summary={funnelOverlay.data?.summary}
                  loading={funnelOverlay.loading}
                  windowFrom={insightsWindow?.from}
                  windowTo={insightsWindow?.to}
                  versionSwitcher={onSwitchVersion && traffickedVersions.versions.length > 0 ? {
                    currentVersionId: versionId,
                    currentVersionName: versionName,
                    versions: traffickedVersions.versions,
                    onSwitch: onSwitchVersion,
                  } : undefined}
                  filterChips={buildAppliedFilterChips(insightsFilters, funnelOverlay.userTraitOptions)
                    .map(chip => ({
                      id: chip.id,
                      label: chip.label,
                      onRemove: () => setInsightsFilters(chip.next),
                    }))}
                  filterZoneNote={(() => {
                    // Pre-answer zone (server-stamped): say WHERE the filtered
                    // numbers become observable — anchor screen names resolve
                    // from the registry the editor already holds.
                    const overlayNodes = funnelOverlay.data?.nodes ?? [];
                    if (!overlayNodes.some(node => node.filterObservability === 'pre')) return undefined;
                    const anchors = overlayNodes
                      .filter(node => node.filterObservability === 'anchor')
                      .map(node => {
                        const step = v2ScreenRegistry.get(node.screenId);
                        return typeof step?.name === 'string' && step.name ? step.name : node.screenId.slice(0, 8);
                      });
                    const anchorLabel = anchors.length > 0
                      ? anchors.slice(0, 2).map(name => `«${name}»`).join(', ') + (anchors.length > 2 ? '…' : '')
                      : 'the answer screen';
                    return `Filtered by an answer — funnel numbers start at ${anchorLabel}; earlier screens show the cohort's path only.`;
                  })()}
                />
                {/* I4 zero-traffic landing (FP-6): the opened version carried no
                    traffic in the window while other versions did — offer the
                    top-starts version instead of a silent zero canvas. */}
                {onSwitchVersion
                  && !traffickedVersions.loading
                  && traffickedVersions.versions.length > 0
                  && !traffickedVersions.versions.some(candidate => candidate.versionId === versionId) && (
                  <Alert
                    severity="warning"
                    data-testid={ANALYTICS_CANVAS_TEST_IDS.noTrafficWarning}
                    action={(
                      <Button
                        data-testid={ANALYTICS_CANVAS_TEST_IDS.noTrafficSwitchTop}
                        color="inherit"
                        size="small"
                        onClick={() => onSwitchVersion(traffickedVersions.versions[0].versionId)}
                      >
                        {`Switch to ${traffickedVersions.versions[0].versionName
                          || traffickedVersions.versions[0].versionId.slice(0, 8)} · ${traffickedVersions.versions[0].starts.toLocaleString('en-US')} starts`}
                      </Button>
                    )}
                  >
                    This version had no traffic in the selected window.
                  </Alert>
                )}
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <ReadOnlyFunnelCanvas
                    nodes={analyticsSnapshot?.nodes ?? []}
                    edges={analyticsSnapshot?.edges ?? []}
                    analyticsOverlay={analyticsOverlay}
                    screenBoundVariables={screenBoundVariables}
                    onNodeClick={handleAnalyticsNodeClick}
                    onFlowLossClick={handleFlowLossClick}
                    onAnalyticsEdgeClick={handleAnalyticsEdgeClick}
                    gridStepSize={16}
                    className="onboarding-canvas"
                  />
                </Box>
              </Box>
            ) : (
              <ReactFlowCanvasProvider>
                <ReactFlowCanvas
                  coordinator={coordinator!}
                  selectedScreenIds={selectedScreenIds}
                  launchScreenId={launchScreenId}
                  onScreensSelected={handleScreensSelected}
                  onScreensMoved={handleScreensMoved}
                  onConnectionCompleted={handleConnectionCompleted}
                  onScreenDropped={handleScreenDropped}
                  onScreensDeleted={handleScreensDeleted}
                  onEdgesDeleted={handleEdgesDeleted}
                  onEdgeClicked={handleEdgeClicked}
                  onAnalyticsEdgeClick={handleAnalyticsEdgeClick}
                  onNodeDoubleClick={handleNodeDoubleClick}
                  onNodeAction={handleNodeAction}
                  onReady={handleCanvasReady}
                  nodeViewFactory={nodeViewFactory}
                  analyticsOverlay={analyticsOverlay}
                  gridStepSize={16}
                  className="onboarding-canvas"
                />
              </ReactFlowCanvasProvider>
            )}
          </CanvasBadgesContext.Provider>
          </CanvasVariablesContext.Provider>
          </CanvasLocaleContext.Provider>

          <Snackbar
            open={Boolean(pasteWarning)}
            autoHideDuration={9000}
            onClose={() => setPasteWarning(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              severity="warning"
              variant="filled"
              onClose={() => setPasteWarning(null)}
              sx={{ maxWidth: 720, whiteSpace: 'pre-line' }}
            >
              {pasteWarning}
            </Alert>
          </Snackbar>

          <Snackbar
            open={Boolean(applyThemeSummary)}
            autoHideDuration={6000}
            onClose={() => setApplyThemeSummary(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              severity="success"
              variant="filled"
              onClose={() => setApplyThemeSummary(null)}
              data-testid="apply-theme-summary-toast"
            >
              {applyThemeSummary}
            </Alert>
          </Snackbar>

          {/* Onboarding theme switch: says what changed AND what did not (screens keep their look). */}
          <Snackbar
            open={Boolean(themeChangedName)}
            autoHideDuration={10000}
            onClose={(_event, reason) => {
              if (reason !== 'clickaway') setThemeChangedName(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              severity="success"
              variant="filled"
              data-testid="onboarding-theme-changed-toast"
              sx={{ alignItems: 'center' }}
              action={(
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {/* A read-only (published / older) version is never restyled in place. */}
                  {!frozen && !viewingOnly && (
                    <Button
                      color="inherit"
                      variant="outlined"
                      size="small"
                      onClick={() => { void handleRestyleAfterThemeChange(); }}
                      data-testid="onboarding-theme-changed-restyle"
                      sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
                    >
                      Restyle screens…
                    </Button>
                  )}
                  <IconButton
                    aria-label="Close"
                    color="inherit"
                    size="small"
                    onClick={() => setThemeChangedName(null)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            >
              {`Theme changed to "${themeChangedName ?? ''}". Existing screens kept their look.`}
            </Alert>
          </Snackbar>

          {/* Tasks Panel — bottom-left, scoped to strategy+block when in block-screens mode.
              Only shown in strategy mode. In regular onboarding mode (no strategyId) the
              panel would have no entity scope and would leak every project task — so we
              hide it entirely rather than mounting an unscoped floating panel. */}
          {strategyId && (
            <CanvasTasksPanel
              projectId={projectId}
              strategyId={strategyId}
              blockId={strategyBlockId}
            />
          )}

          {/* TransitionEditor Dialog */}
          <TransitionEditor
            open={editorOpen}
            onClose={() => setEditorOpen(false)}
            edgeData={selectedEdge}
            onEdgeDelete={handleEdgeDeleteFromDialog}
            onEditConditions={handleEditConditions}
            onTransitionSave={handleEdgeTransitionSave}
          />

          {/* V2ConditionEditorDialog — variable-based conditions with AND/OR groups */}
          {conditionEditorData && flowDocument && (
            <V2ConditionEditorDialog
              open={conditionEditorOpen}
              onClose={() => {
                setConditionEditorOpen(false);
                setConditionEditorData(null);
              }}
              sourceStepId={conditionEditorData.sourceScreenId}
              targetStepId={conditionEditorData.targetScreenId}
              sourceStepName={conditionEditorData.sourceStepName}
              targetStepName={conditionEditorData.targetStepName}
              currentConditions={conditionEditorData.currentConditions}
              onSave={handleConditionsSave}
              flowDocument={flowDocument}
            />
          )}

          {/* Edge Delete Confirmation */}
          <EdgeDeleteConfirmation
            open={showEdgeDeleteConfirm}
            onClose={() => setShowEdgeDeleteConfirm(false)}
            onConfirm={confirmEdgeDeletion}
            message={
              edgesToDelete.length === 1
                ? 'Are you sure you want to delete this connection?'
                : `Are you sure you want to delete ${edgesToDelete.length} connections?`
            }
          />

          <FunnelInsightsEdgeDrawer
            open={Boolean(selectedInsightsEdge)}
            metric={selectedInsightsEdge}
            drilldown={edgeDrilldown.data}
            loading={edgeDrilldown.loading}
            error={edgeDrilldown.error}
            warnings={edgeDrilldown.warnings}
            onClose={() => setSelectedInsightsEdge(null)}
          />

          {/* Spec D D1b: per-node metrics drawer (analytics mode node click) */}
          <AnalyticsNodeDrawer
            open={Boolean(selectedInsightsNode)}
            metric={selectedInsightsNode?.metric ?? null}
            screenName={selectedInsightsNode?.screenName}
            screenType={selectedInsightsNode?.screenType}
            windowDays={funnelOverlay.data?.summary?.windowDays}
            answers={selectedInsightsNode
              ? funnelOverlay.screenAnswers?.[selectedInsightsNode.metric.screenId] ?? null
              : null}
            answersAvailable={funnelOverlay.screenAnswersAvailable}
            productLabels={selectedInsightsNode
              ? extractPaywallProductLabels(v2ScreenRegistry.get(selectedInsightsNode.metric.screenId))
              : undefined}
            onClose={() => setSelectedInsightsNode(null)}
          />

          {/* I-A: drain-stub click → «Left after {screen}» (leavers vs reached) */}
          <DropoffAudienceDrawer
            open={Boolean(flowLossScreenId)}
            onClose={() => setFlowLossScreenId(null)}
            screenName={flowLossScreenId
              ? (() => {
                const step = v2ScreenRegistry.get(flowLossScreenId);
                return typeof step?.name === 'string' ? step.name : flowLossScreenId;
              })()
              : undefined}
            screenType={flowLossScreenId ? v2ScreenRegistry.get(flowLossScreenId)?.screenType : undefined}
            metric={flowLossScreenId ? analyticsOverlay?.nodesByScreenId[flowLossScreenId] ?? null : null}
            audience={flowLossDrilldown.data}
            loading={flowLossDrilldown.loading}
            error={flowLossDrilldown.error}
            filtersActive={countAppliedFilterClauses(insightsFilters) > 0}
          />

          {/* V2 Screen Editor Dialog — works directly with StepNode (Sprint 3) */}
          {coordinator && (
            <OnboardingScreenEditorDialogV2
              open={screenEditorOpen}
              onClose={() => {
                if (autoOpenScreenId && editingEntityIdRef.current === autoOpenScreenId) {
                  autoOpenTriggeredRef.current = true;
                  lastAutoOpenScreenIdRef.current = autoOpenScreenId;
                }
                setScreenEditorOpen(false);
                setSelectedStepForEdit(null);
                editingEntityIdRef.current = null;
                onScreenEditorClose?.();
              }}
              stepData={selectedStepForEdit}
              projectId={projectId}
              onboardingId={onboardingId}
              versionId={versionId}
              defaultLanguage={effectiveDefaultLanguage}
              languages={effectiveLanguages}
              onStepSaved={handleEditorStepSaved}
              screensBindingMap={screensBindingMap}
              flowDocument={flowDocument}
              themeSettingsV2={themeSettingsV2}
              mode={editorMode}
              strategyId={strategyId}
              strategyBlockId={strategyBlockId}
              platform={platform}
              variablesAdapter={variablesHost ?? undefined}
            />
          )}


          {isOnboardingHost && (
            <BaseDialog
              open={strategyAssociationDialogOpen}
              onClose={() => {
                if (!strategyAssociationSaving) {
                  setStrategyAssociationDialogOpen(false);
                }
              }}
              maxWidth="sm"
              fullWidth
              mode="modal"
              data-testid="onboarding-strategy-association-dialog"
              aria-labelledby="onboarding-strategy-association-title"
            >
              <DialogTitle
                id="onboarding-strategy-association-title"
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
              >
                <Typography variant="h6">Associate Strategy</Typography>
                <IconButton
                  aria-label="close strategy association"
                  size="small"
                  disabled={strategyAssociationSaving}
                  onClick={() => setStrategyAssociationDialogOpen(false)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>
              <Divider />
              <DialogContent>
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    This links the current onboarding version to a strategy. Bulk block assignment will use that strategy's blocks.
                  </Typography>
                  {targetStrategyIdForPaste && (
                    <Alert severity="info">
                      Current association: {associatedStrategy?.name || targetStrategyIdForPaste}
                    </Alert>
                  )}
                  {strategyAssociationError && (
                    <Alert severity="error">{strategyAssociationError}</Alert>
                  )}
                  <Autocomplete
                    options={availableStrategies}
                    loading={strategyListLoading}
                    value={selectedStrategyForAssociation}
                    onChange={(_, value) => setSelectedStrategyForAssociation(value)}
                    getOptionLabel={(strategyOption) => {
                      const title = strategyOption.name || strategyOption.id;
                      return title === strategyOption.id ? title : `${title} (${strategyOption.id})`;
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(options, state) => {
                      const query = state.inputValue.trim().toLowerCase();
                      if (!query) return options;
                      return options.filter((strategyOption) => (
                        strategyOption.id.toLowerCase().includes(query)
                        || (strategyOption.name || '').toLowerCase().includes(query)
                        || (strategyOption.description || '').toLowerCase().includes(query)
                      ));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Strategy"
                        placeholder={strategyListLoading ? 'Loading strategies...' : 'Search strategies'}
                        size="small"
                        inputProps={{
                          ...params.inputProps,
                          'data-testid': 'onboarding-strategy-association-input',
                        }}
                      />
                    )}
                  />
                </Stack>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                  onClick={() => setStrategyAssociationDialogOpen(false)}
                  disabled={strategyAssociationSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleStrategyAssociationSave}
                  disabled={strategyAssociationSaving || !selectedStrategyForAssociation}
                  data-testid="onboarding-strategy-association-save"
                >
                  {strategyAssociationSaving ? 'Saving...' : 'Associate'}
                </Button>
              </DialogActions>
            </BaseDialog>
          )}

          {/* Set Launch Entity Confirmation Dialog */}
          {confirmDialog && (
            <BaseDialog
              open={true}
              onClose={() => {
                confirmDialog.resolve(false);
                setConfirmDialog(null);
              }}
              title="Confirmation"
              maxWidth="sm"
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {confirmDialog.message}
                </Typography>
                {confirmDialog.details && (
                  <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
                    {confirmDialog.details}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => {
                      confirmDialog.resolve(true);
                      setConfirmDialog(null);
                    }}
                  >
                    Continue
                  </Button>
                </Box>
              </Box>
            </BaseDialog>
          )}
        </Box>
      </Box>

      {!strategyId && targetStrategyIdForPaste && (
        <StrategyCanvasEditor
          open={associatedStrategyOverlayOpen}
          onClose={() => setAssociatedStrategyOverlayOpen(false)}
          projectId={projectId}
          strategyId={targetStrategyIdForPaste}
          strategyName={associatedStrategy?.name}
        />
      )}

      {/* Right: V2 Template Panel (native V2 StepNode templates; hidden in read-only analytics mode) */}
      {!analyticsModeActive && (
        <OnboardingTemplatePanelV2
          ref={templatePanelRef}
          projectId={projectId}
          onboardingId={onboardingId}
          versionId={versionId}
          defaultLanguage={effectiveDefaultLanguage}
          languages={effectiveLanguages}
          templates={templates}
          externalThemeSettings={strategyId ? themeSettingsV2 : undefined}
          onExternalThemeSettingsSave={strategyId ? updateStrategyTheme : undefined}
          onTemplateDragStart={() => {}}
          onApplyThemeToCanvas={!strategyId ? handleApplyDraftThemeByTypes : undefined}
          onTemplateClick={!strategyId ? handleAddScreenByTemplateId : undefined}
        />
      )}

      {/* Click-to-add screen picker (Cmd/Ctrl+K) */}
      {isOnboardingHost && (
        <ScreenPickerDialog
          open={screenPickerOpen}
          onClose={() => setScreenPickerOpen(false)}
          screens={pickerTemplates}
          projectId={projectId}
          locale={effectiveDefaultLanguage}
          onPick={(screen) => handleAddScreenByTemplateId(screen.id)}
        />
      )}

      {/* Selective theme apply confirm dialog (shared by both surfaces) */}
      {applyThemeDialog && (
        <ApplyThemeGroupsDialog
          open
          onClose={() => setApplyThemeDialog(null)}
          theme={applyThemeDialog.theme}
          screens={applyThemeDialog.screens}
          sourceLabel={applyThemeDialog.sourceLabel}
          scopeLabel={applyThemeDialog.scopeLabel}
          locale={effectiveDefaultLanguage}
          allowThemeEdit={applyThemeDialog.allowThemeEdit}
          projectId={projectId}
          templateIdByKind={applyThemeDialog.templateIdByKind}
          initialGroups={applyThemeDialog.initialGroups}
          onConfirm={persistAppliedThemeScreens}
        />
      )}

      {strategyId && (
        <StrategyThemeSelectionDialog
          open={themeReplaceOpen}
          onClose={() => {
            if (!themeReplaceLoading) setThemeReplaceOpen(false);
          }}
          title="Select Strategy Theme"
          options={themeReplaceOptions}
          selectedOption={selectedThemeReplaceOption}
          projectId={projectId}
          loading={themeReplaceLoading}
          error={themeReplaceError}
          onSelect={setSelectedThemeReplaceOption}
          onConfirm={handleConfirmReplaceTheme}
        />
      )}

      {!strategyId && isOnboardingHost && onboardingId && (
        <StrategyThemeSelectionDialog
          mode="onboarding"
          open={themeReplaceOpen}
          onClose={() => {
            if (!themeReplaceLoading) setThemeReplaceOpen(false);
          }}
          options={themeReplaceOptions}
          selectedOption={selectedThemeReplaceOption}
          currentOptionKey={themeReplaceCurrentKey}
          projectId={projectId}
          loading={themeReplaceLoading}
          error={themeReplaceError}
          onSelect={(option) => {
            setThemeReplaceError(null);
            setSelectedThemeReplaceOption(option);
          }}
          onConfirm={() => { void handleConfirmChangeOnboardingTheme(); }}
        />
      )}

      {/* Block import dialog (strategy mode only) */}
      {strategyId && strategyBlockId && (
        <StrategyImportDialog
          open={blockImportOpen}
          onClose={() => setBlockImportOpen(false)}
          mode="block"
          projectId={projectId}
          strategyId={strategyId}
          blockId={strategyBlockId}
          themeFlowDocument={themeSettingsV2?.flowDocument}
          onImported={handleBlockImported}
        />
      )}

      {/* Per-block pull/push confirmation (strategy mode only) */}
      {strategyId && strategyBlockId && (
        <StrategySyncConfirmDialog
          open={Boolean(syncDialogMode)}
          mode={syncDialogMode ?? 'pull-block'}
          dryRunResult={syncDryRun}
          loading={syncLoading}
          error={syncError}
          onConfirm={handleConfirmBlockSync}
          onClose={() => setSyncDialogMode(null)}
        />
      )}

      {/* Spin animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
    </FontConfigFromProjectProvider>
    </FontLabelsProvider>
    </FunnelUnitProvider>
  );

  // Embedded mode — render directly
  if (embedded) {
    return (
      <Box sx={{ height: '100%', bgcolor: 'background.default' }}>
        {canvasContent}
      </Box>
    );
  }

  // Normal mode — render in Portal
  return (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1300,
          bgcolor: 'background.default',
        }}
      >
        {canvasContent}
      </Box>
    </Portal>
  );
};

export default OnboardingCanvasEditorV2;
